import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Alert,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Autocomplete
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon,
  CheckCircle, 
  Cancel,
  LocalFlorist, 
  Speed, 
  Palette, 
  LocalShipping, 
  SupportAgent, 
  Verified, 
  CardGiftcard, 
  Security, 
  SentimentSatisfiedAlt, 
  AccessTime, 
  Star,
  ArrowUpward,
  ArrowDownward,
  CreditCard
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
  import {
    collection,
    addDoc,
    onSnapshot,
    doc,
    deleteDoc,
    updateDoc,
    arrayUnion,
    orderBy,
    query,
    collectionGroup,
    setDoc,
    getDoc,
    getDocs,
    serverTimestamp,
  } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, uploadBytesResumable, getDownloadURL, listAll, list } from 'firebase/storage';
import SalesDashboard from '../components/SalesDashboard';

const ICON_OPTIONS: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: 'speed', label: 'Velocidad', icon: <Speed /> },
  { value: 'palette', label: 'Arte', icon: <Palette /> },
  { value: 'flower', label: 'Flor', icon: <LocalFlorist /> },
  { value: 'shipping', label: 'Envío', icon: <LocalShipping /> },
  { value: 'support', label: 'Soporte', icon: <SupportAgent /> },
  { value: 'verified', label: 'Verificado', icon: <Verified /> },
  { value: 'gift', label: 'Regalo', icon: <CardGiftcard /> },
  { value: 'security', label: 'Seguridad', icon: <Security /> },
  { value: 'happy', label: 'Felicidad', icon: <SentimentSatisfiedAlt /> },
  { value: 'time', label: 'Tiempo', icon: <AccessTime /> },
  { value: 'star', label: 'Estrella', icon: <Star /> },
];

interface ProductDiscount {
  active: boolean;
  type: 'quantity' | 'date';
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  limitCount?: number;
  soldCount?: number;
  startDate?: string;
  endDate?: string;
}

const AdminPage = () => {
  const { user, login } = useAuth();
  const [tab, setTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Categorías
  const [categories, setCategories] = useState<any[]>([]);
  const [catName, setCatName] = useState('');
  const [catSlug, setCatSlug] = useState('');
  const [catImageUrl, setCatImageUrl] = useState('');
  const [catImageFile, setCatImageFile] = useState<File | null>(null);
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<string>('');
  const [editCategoryForm, setEditCategoryForm] = useState<{ name: string; slug: string; imageUrl: string }>({ name: '', slug: '', imageUrl: '' });
  const [editCategoryImageFile, setEditCategoryImageFile] = useState<File | null>(null);

  // Productos
  const [products, setProducts] = useState<any[]>([]);
  const [prodForm, setProdForm] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    imageUrl: '',
    stock: '',
  });
  const [prodError, setProdError] = useState('');
  const [editingProductId, setEditingProductId] = useState<string>('');
  const [editProductForm, setEditProductForm] = useState<{ name: string; description: string; price: string; categoryId: string; imageUrl: string; stock: string }>({ name: '', description: '', price: '', categoryId: '', imageUrl: '', stock: '' });

  // Variantes de producto
  const [prodVariants, setProdVariants] = useState<{ quantity: number; price: number }[]>([]);
  const [prodVarQty, setProdVarQty] = useState<string>('');
  const [prodVarPrice, setProdVarPrice] = useState<string>('');
  const [editVariants, setEditVariants] = useState<{ quantity: number; price: number }[]>([]);
  const [editVarQty, setEditVarQty] = useState<string>('');
  const [editVarPrice, setEditVarPrice] = useState<string>('');
  const [prodHasVariant, setProdHasVariant] = useState<boolean>(false);
  const [editHasVariant, setEditHasVariant] = useState<boolean>(false);
  const [prodMainVarQty, setProdMainVarQty] = useState<string>('');
  const [editMainVarQty, setEditMainVarQty] = useState<string>('');

  // Promociones / Descuentos
  const [prodDiscount, setProdDiscount] = useState<ProductDiscount>({
    active: false,
    type: 'quantity',
    discountType: 'percentage',
    discountValue: 0,
    limitCount: 0,
    soldCount: 0
  });
  
  const [editDiscount, setEditDiscount] = useState<ProductDiscount>({
    active: false,
    type: 'quantity',
    discountType: 'percentage',
    discountValue: 0,
    limitCount: 0,
    soldCount: 0
  });

  // Sincronizar precio de la variante principal con el precio del producto (creación)
  useEffect(() => {
    if (!prodHasVariant) return;
    const basePrice = Number(prodForm.price);
    if (isNaN(basePrice)) return;
    setProdVariants((prev) => {
      if (!prev.length) return prev;
      if (prev[0].price === basePrice) return prev;
      const next = [...prev];
      next[0] = { ...next[0], price: basePrice };
      return next;
    });
  }, [prodForm.price, prodHasVariant]);

  // Sincronizar precio de la variante principal con el precio del producto (edición)
  useEffect(() => {
    if (!editHasVariant) return;
    const basePrice = Number(editProductForm.price);
    if (isNaN(basePrice)) return;
    setEditVariants((prev) => {
      if (!prev.length) return prev;
      if (prev[0].price === basePrice) return prev;
      const next = [...prev];
      next[0] = { ...next[0], price: basePrice };
      return next;
    });
  }, [editProductForm.price, editHasVariant]);

  // Imágenes (por URL sencilla)
  const [imgProductId, setImgProductId] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imgError, setImgError] = useState('');
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [imgUploadLoading, setImgUploadLoading] = useState(false);
  const newImgInputRef = useRef<HTMLInputElement>(null);
  // Edición de imágenes existentes
  const [editImgExistingUrl, setEditImgExistingUrl] = useState('');
  const [editImgNewUrl, setEditImgNewUrl] = useState('');
  const [editImgSetAsMain, setEditImgSetAsMain] = useState(false);
  const [editImgFile, setEditImgFile] = useState<File | null>(null);
  const [editImgLoading, setEditImgLoading] = useState(false);
  // Listado de imágenes en Storage por producto
  const [storageImages, setStorageImages] = useState<string[]>([]);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageListError, setStorageListError] = useState('');

  // Pedidos (collectionGroup)
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersError, setOrdersError] = useState('');
  const [ordersNotice, setOrdersNotice] = useState('');
  const [ordersModalList, setOrdersModalList] = useState<any[]>([]);
  const ordersModalUnsubRef = useRef<(() => void) | null>(null);
  const [orderCountsByUser, setOrderCountsByUser] = useState<Record<string, number>>({});
  const [useOrdersClientFallback, setUseOrdersClientFallback] = useState(false);
  const ordersAggUnsubsRef = useRef<(() => void)[]>([]);
  const ordersAggBufferRef = useRef<Record<string, any[]>>({});
  const [openOrderDetail, setOpenOrderDetail] = useState<any | null>(null);

  // Zonas de reparto (documento único en settings)
  const [zoneCities, setZoneCities] = useState('');
  const [zoneZipCodes, setZoneZipCodes] = useState('');
  // Cuenta Clabe y Datos Bancarios
  const [clabe, setClabe] = useState('');
  const [bankName, setBankName] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [clabeLoading, setClabeLoading] = useState(false);
  const [clabeError, setClabeError] = useState('');
  const [clabeSuccess, setClabeSuccess] = useState('');
  const [zoneShippingCost, setZoneShippingCost] = useState('');
  const [zoneError, setZoneError] = useState('');
  const [zoneSuccess, setZoneSuccess] = useState('');
  // Bloqueo de ciudad y gestión por CP para Atlixco
  const [zoneCityLocked] = useState<string>('Atlixco');
  type ZipCodeEntry = { zipCode: string; colonias: string[]; cost: number };
  const [zipCodeEntries, setZipCodeEntries] = useState<ZipCodeEntry[]>([]);
  const [autoFillColonias, setAutoFillColonias] = useState<boolean>(true);

  const fetchColoniasByCP = async (cp: string): Promise<string[]> => {
    const versions = ['latest', '20241116', '20241009', '20240320', '20240120'];
    for (const v of versions) {
      const url = v === 'latest'
        ? `https://sepomex.nitrostudio.com.mx/api/latest/cp/${cp}.json`
        : `https://sepomex.nitrostudio.com.mx/api/${v}/cp/${cp}.json`;
      try {
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!res.ok) continue;
        const json = await res.json();
        const list: string[] = (json?.data?.postcodes || [])
          .filter((p: any) => String(p?.d_mnpio || '').toLowerCase() === 'atlixco')
          .map((p: any) => String(p?.d_asenta || '').trim())
          .filter(Boolean);
        const unique = Array.from(new Set(list));
        if (unique.length > 0) return unique;
      } catch (e) {
        console.warn('[Admin] SEPOMEX fallo', v, cp, e);
        // intentar siguiente versión
      }
    }
    return [];
  };
  // Estado para destinatarios (recipients) por usuario (Clientes)
  const [recipientsByUser, setRecipientsByUser] = useState<Record<string, any[]>>({});
  const [recipientsError, setRecipientsError] = useState('');

  // Clientes (usuarios)
  const [clients, setClients] = useState<any[]>([]);
  const [clientsError, setClientsError] = useState('');
  const [clientsSort, setClientsSort] = useState<'date' | 'alpha' | 'email'>('date');

  // Menú secundario y configuración de Diseño
  const [activeSection, setActiveSection] = useState<'admin' | 'design' | 'clients' | 'realtime' | 'previous' | 'coupons' | 'push' | 'sales' | 'stripe' | 'bank_details' | 'telegram'>('admin');
  // Estado para Notificaciones Push
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushImageUrl, setPushImageUrl] = useState('');
  const [pushDeepLink, setPushDeepLink] = useState('');
  const [pushTarget, setPushTarget] = useState<'all' | 'userId'>('all');
  const [pushUserId, setPushUserId] = useState('');
  const [pushError, setPushError] = useState('');
  const [pushSuccess, setPushSuccess] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const handleSendPush = async () => {
    setPushError('');
    setPushSuccess('');
    try {
      if (!isAdmin) { setPushError('No tienes permisos para enviar notificaciones.'); return; }
      const title = (pushTitle || '').trim();
      const body = (pushBody || '').trim();
      if (!title || !body) { setPushError('Título y cuerpo son requeridos.'); return; }
      if (pushTarget === 'userId' && !(pushUserId || '').trim()) { setPushError('Ingresa el UID de destino.'); return; }
      setPushSending(true);
      // Esquema simple de cola en Firestore (procesado por backend/Cloud Function externo)
      const payload: any = {
        title,
        body,
        imageUrl: (pushImageUrl || '').trim() || null,
        deepLink: (pushDeepLink || '').trim() || null,
        target: pushTarget,
        userId: pushTarget === 'userId' ? (pushUserId || '').trim() : null,
        createdAt: Date.now(),
        status: 'pending'
      };
      await addDoc(collection(db, 'pushQueue'), payload);
      setPushSuccess('Notificación encolada correctamente.');
      setPushTitle(''); setPushBody(''); setPushImageUrl(''); setPushDeepLink(''); setPushTarget('all'); setPushUserId('');
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      setPushError(`Error al encolar notificación (${code}).`);
    } finally {
      setPushSending(false);
    }
  };

  // Stripe Configuration
  const [stripePublicKey, setStripePublicKey] = useState('');
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeError, setStripeError] = useState('');
  const [stripeSuccess, setStripeSuccess] = useState('');
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchStripeKeys = async () => {
        try {
            // Fetch Public Key
            const publicRef = doc(db, 'settings', 'stripe_public');
            const publicSnap = await getDoc(publicRef);
            if (publicSnap.exists()) {
                const data = publicSnap.data();
                if (data.publicKey) setStripePublicKey(data.publicKey);
            }

            // Fetch Secret Key
            const secretRef = doc(db, 'settings', 'stripe_secret');
            const secretSnap = await getDoc(secretRef);
            if (secretSnap.exists()) {
                const data = secretSnap.data();
                if (data.secretKey) setStripeSecretKey(data.secretKey);
            }
        } catch (e) {
            console.warn('Error fetching stripe keys', e);
        }
    };
    fetchStripeKeys();
  }, [isAdmin]);

  const handleSaveStripeKeys = async () => {
      setStripeError('');
      setStripeSuccess('');
      setStripeLoading(true);
      try {
          // Basic validation
          if (stripeSecretKey && !stripeSecretKey.startsWith('sk_')) {
               setStripeError('La llave secreta debe comenzar con sk_');
               setStripeLoading(false);
               return;
          }
          if (stripePublicKey && !stripePublicKey.startsWith('pk_')) {
              setStripeError('La llave pública debe comenzar con pk_');
              setStripeLoading(false);
              return;
         }

          // Save Public Key
          await setDoc(doc(db, 'settings', 'stripe_public'), {
              publicKey: stripePublicKey.trim(),
              updatedAt: Date.now(),
              updatedBy: user?.id || 'admin'
          });

          // Save Secret Key
          await setDoc(doc(db, 'settings', 'stripe_secret'), {
              secretKey: stripeSecretKey.trim(),
              updatedAt: Date.now(),
              updatedBy: user?.id || 'admin'
          });
          setStripeSuccess('Llaves de Stripe actualizadas correctamente.');
      } catch (e) {
          console.error(e);
          setStripeError('Error al guardar las llaves.');
      } finally {
          setStripeLoading(false);
      }
  };

  // Telegram Configuration
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [telegramError, setTelegramError] = useState('');
  const [telegramSuccess, setTelegramSuccess] = useState('');
  const [telegramLoading, setTelegramLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchTelegramConfig = async () => {
      try {
        const ref = doc(db, 'settings', 'telegram_secret');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as any;
          if (typeof data.token === 'string') setTelegramToken(data.token);
          if (typeof data.chatId === 'string') setTelegramChatId(data.chatId);
        }
      } catch (e) {
        console.warn('[Admin] No se pudo cargar configuracion de Telegram', e);
      }
    };
    fetchTelegramConfig();
  }, [isAdmin]);

  const handleSaveTelegram = async () => {
    setTelegramError('');
    setTelegramSuccess('');
    setTelegramLoading(true);
    try {
      if (!telegramToken.trim() || !telegramChatId.trim()) {
        setTelegramError('Ingresa token y chatId.');
        setTelegramLoading(false);
        return;
      }
      await setDoc(doc(db, 'settings', 'telegram_secret'), {
        token: telegramToken.trim(),
        chatId: telegramChatId.trim(),
        updatedAt: Date.now(),
        updatedBy: user?.id || 'admin'
      }, { merge: true });
      setTelegramSuccess('Configuración de Telegram guardada.');
    } catch (e) {
      console.error(e);
      setTelegramError('Error al guardar configuración.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    setTelegramError('');
    setTelegramSuccess('');
    try {
      const res = await fetch('/api/orders/telegram-notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order: {
            id: 'TEST-' + Math.random().toString(36).slice(2, 8).toUpperCase(),
            userId: user?.id || 'admin',
            userEmail: user?.email || '',
            sender: { name: user?.name || 'Admin', email: user?.email || '', phone: '' },
            items: [
              { name: 'Prueba Telegram', quantity: 1, price: 1 }
            ],
            subtotal: 1,
            shippingCost: 0,
            total: 1,
            shipping: {
              recipientName: 'Destino Prueba',
              recipientPhone: '',
              street: '',
              colonia: '',
              city: '',
              state: '',
              zipCode: '',
              deliveryDate: '',
              deliveryTime: ''
            },
            payment: { method: 'cash' },
            status: 'pending'
          }
        })
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json?.message || 'Fallo envío de prueba');
      }
      setTelegramSuccess(`Mensaje(s) enviados: ${json.sent}`);
    } catch (e: any) {
      setTelegramError(e?.message || 'Error en prueba de Telegram');
    }
  };

  const navigate = useNavigate();
  const [designPrimaryColor, setDesignPrimaryColor] = useState('#e91e63');
  const [designAccentColor, setDesignAccentColor] = useState('#c2185b');
  const [designFooterColor, setDesignFooterColor] = useState('#121212');
  const [designLogoColor, setDesignLogoColor] = useState('#ffffff');
  const [designButtonColor, setDesignButtonColor] = useState('');
  const [designLogoUrl, setDesignLogoUrl] = useState('');
  const [designFaviconUrl, setDesignFaviconUrl] = useState('');
  const [designHeroTitle, setDesignHeroTitle] = useState('');
  const [designHeroSubtitle, setDesignHeroSubtitle] = useState('');
  const [designBannerUrl, setDesignBannerUrl] = useState('');
  const [designError, setDesignError] = useState('');
  const [designSuccess, setDesignSuccess] = useState('');
  const [designLogoFile, setDesignLogoFile] = useState<File | null>(null);
  const [designLogoUploading, setDesignLogoUploading] = useState(false);
  const [designLogoUploadError, setDesignLogoUploadError] = useState('');
  
  const [designFaviconFile, setDesignFaviconFile] = useState<File | null>(null);
  const [designFaviconUploading, setDesignFaviconUploading] = useState(false);
  const [designFaviconUploadError, setDesignFaviconUploadError] = useState('');

  const [designHomeImages, setDesignHomeImages] = useState<string[]>([]);
  const [designHomeImageFile, setDesignHomeImageFile] = useState<File | null>(null);
  const [designHomeImageUploading, setDesignHomeImageUploading] = useState(false);
  const [designHomeImageUploadError, setDesignHomeImageUploadError] = useState('');

  const [designAllCategoryImageUrl, setDesignAllCategoryImageUrl] = useState('');
  const [designAllCategoryImageFile, setDesignAllCategoryImageFile] = useState<File | null>(null);
  const [designAllCategoryImageUploading, setDesignAllCategoryImageUploading] = useState(false);
  const [designAllCategoryImageUploadError, setDesignAllCategoryImageUploadError] = useState('');

  const [designSupportPhone, setDesignSupportPhone] = useState('522222222222');

  const [designHeaderColor, setDesignHeaderColor] = useState('');
  const [designServicesSectionColor, setDesignServicesSectionColor] = useState('');
  const [designServicesContent, setDesignServicesContent] = useState<any[]>([
    { icon: 'speed', title: 'Envío Express', description: 'Entrega en 2 horas en zonas seleccionadas' },
    { icon: 'palette', title: 'Diseño Exclusivo', description: 'Arreglos únicos creados por expertos floristas' },
    { icon: 'flower', title: 'Frescura Garantizada', description: 'Seleccionamos las flores más frescas cada día' }
  ]);
  const [designAboutTitle, setDesignAboutTitle] = useState('');
  const [designAboutDescription, setDesignAboutDescription] = useState('');
  const [designAboutImageUrl, setDesignAboutImageUrl] = useState('');
  const [designAboutImageFile, setDesignAboutImageFile] = useState<File | null>(null);
  const [designAboutImageUploading, setDesignAboutImageUploading] = useState(false);
  const [designAboutImageUploadError, setDesignAboutImageUploadError] = useState('');

  const [designFooterDescription, setDesignFooterDescription] = useState('');
  const [designFooterPhone, setDesignFooterPhone] = useState('');
  const [designFooterEmail, setDesignFooterEmail] = useState('');
  const [designFooterHours, setDesignFooterHours] = useState('');
  const [designFooterFacebook, setDesignFooterFacebook] = useState('');
  const [designFooterInstagram, setDesignFooterInstagram] = useState('');
  const [designFooterWhatsapp, setDesignFooterWhatsapp] = useState('');

  // UI de Clientes
  const [openAddressesClientId, setOpenAddressesClientId] = useState<string | null>(null);
  const [openOrdersClientId, setOpenOrdersClientId] = useState<string | null>(null);

  // Checar si el usuario es admin leyendo settings/admins
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        if (!user) {
          setIsAdmin(false);
          setAuthChecked(true);
          return;
        }
        const ref = doc(db, 'settings', 'admins');
        const snap = await getDoc(ref);
        const data = snap.exists() ? (snap.data() as any) : {};
        const uids: string[] = Array.isArray(data.uids) ? data.uids : [];
        setIsAdmin(uids.includes(user.id));
      } catch (e) {
        console.warn('[Admin] No se pudo verificar admin', e);
        setIsAdmin(false);
      } finally {
        setAuthChecked(true);
      }
    };
    checkAdmin();
  }, [user]);

  // Suscripciones solo si es admin
  useEffect(() => {
    if (!isAdmin) return;
    // Categorías
    const unsubCats = onSnapshot(
      collection(db, 'categories'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a: any, b: any) => {
          const orderA = typeof a.order === 'number' ? a.order : 0;
          const orderB = typeof b.order === 'number' ? b.order : 0;
          return orderA - orderB;
        });
        setCategories(list);
        setCatError('');
      },
      (err) => {
        console.warn('[Admin] Error categorías realtime', err);
        setCatError('No se pudieron cargar las categorías');
      }
    );
    // Productos
    const unsubProds = onSnapshot(
      collection(db, 'products'),
      (snap) => {
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setProdError('');
      },
      (err) => {
        console.warn('[Admin] Error productos realtime', err);
        setProdError('No se pudieron cargar los productos');
      }
    );
    // Zonas (settings/deliveryZones)
    const unsubZones = onSnapshot(doc(db, 'settings', 'deliveryZones'), (snap) => {
      if (snap.exists()) {
        const data: any = snap.data();
        // Compatibilidad hacia atrás
        setZoneCities((data.cities || []).join(', '));
        setZoneZipCodes((data.zipCodes || []).join(', '));
        setZoneShippingCost(
          typeof data.shippingCost === 'number' && data.shippingCost >= 0
            ? String(data.shippingCost)
            : ''
        );
        // Nuevo modelo: zipCodeEntries y ciudad bloqueada
        const entries: ZipCodeEntry[] = Array.isArray(data.zipCodeEntries)
          ? data.zipCodeEntries.map((e: any) => ({
              zipCode: String(e.zipCode || '').trim(),
              colonias: Array.isArray(e.colonias) ? e.colonias.map((c: any) => String(c).trim()).filter(Boolean) : [],
              cost: typeof e.cost === 'number' ? e.cost : Number(e.cost) || 0,
            }))
          : [];
        if (entries.length > 0) {
          setZipCodeEntries(entries);
        } else {
          // Semilla mínima de ejemplo para Atlixco
          setZipCodeEntries([
            { zipCode: '74200', colonias: ['Atlixco Centro'], cost: 79 },
            { zipCode: '74210', colonias: ['Los Ángeles', 'Las Nieves', 'Huerta de San José'], cost: 89 },
            { zipCode: '74250', colonias: ['San Juan de Dios', 'La Soledad'], cost: 99 },
          ]);
        }
      }
    }, (err) => {
      console.warn('[Admin] Error zonas (deliveryZones) realtime', err);
      setZoneError('No se pudieron cargar las zonas de reparto');
    });
    // Clabe (settings/clabe)
    const unsubClabe = onSnapshot(doc(db, 'settings', 'clabe'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setClabe(data.clabe || '');
        setBankName(data.bankName || '');
        setBeneficiary(data.beneficiary || '');
      }
    });
    // Destinatarios (recipients) via collectionGroup
    const unsubRecipients = onSnapshot(
      collectionGroup(db, 'recipients'),
      (snap) => {
        const map: Record<string, any[]> = {};
        snap.docs.forEach((d) => {
          const userId = d.ref.parent.parent?.id || 'unknown';
          if (!map[userId]) map[userId] = [];
          map[userId].push({ id: d.id, ...d.data() });
        });
        setRecipientsByUser(map);
        setRecipientsError('');
      },
      (err) => {
        console.warn('[Admin] Error obteniendo recipients', err);
        setRecipientsError('No se pudieron cargar los destinatarios');
      }
    );
    // Clientes (users)
    const unsubClients = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setClientsError('');
      },
      (err) => {
        console.warn('[Admin] Error obteniendo clientes', err);
        setClientsError('No se pudieron cargar los clientes');
      }
    );
    // Diseño (settings/design)
    const unsubDesign = onSnapshot(doc(db, 'settings', 'design'), (snap) => {
      if (snap.exists()) {
        const d: any = snap.data();
        if (typeof d.primaryColor === 'string') setDesignPrimaryColor(d.primaryColor);
        if (typeof d.accentColor === 'string') setDesignAccentColor(d.accentColor);
        if (typeof d.logoUrl === 'string') setDesignLogoUrl(d.logoUrl);
        if (typeof d.faviconUrl === 'string') setDesignFaviconUrl(d.faviconUrl);
        if (typeof d.allCategoryImageUrl === 'string') setDesignAllCategoryImageUrl(d.allCategoryImageUrl);
        if (typeof d.supportPhone === 'string') setDesignSupportPhone(d.supportPhone);
        if (typeof d.heroTitle === 'string') setDesignHeroTitle(d.heroTitle);
        if (typeof d.heroSubtitle === 'string') setDesignHeroSubtitle(d.heroSubtitle);
        if (typeof d.bannerUrl === 'string') setDesignBannerUrl(d.bannerUrl);
        if (typeof d.footerColor === 'string') setDesignFooterColor(d.footerColor);
        if (typeof d.logoColor === 'string') setDesignLogoColor(d.logoColor);
        if (typeof d.buttonColor === 'string') setDesignButtonColor(d.buttonColor);
        if (Array.isArray(d.homeImages)) setDesignHomeImages(d.homeImages);
        if (typeof d.headerColor === 'string') setDesignHeaderColor(d.headerColor);
        if (typeof d.servicesSectionColor === 'string') setDesignServicesSectionColor(d.servicesSectionColor);
        if (Array.isArray(d.servicesContent)) setDesignServicesContent(d.servicesContent);
        if (d.aboutUs) {
          if (typeof d.aboutUs.title === 'string') setDesignAboutTitle(d.aboutUs.title);
          if (typeof d.aboutUs.description === 'string') setDesignAboutDescription(d.aboutUs.description);
          if (typeof d.aboutUs.imageUrl === 'string') setDesignAboutImageUrl(d.aboutUs.imageUrl);
        }
        if (d.footer) {
          if (typeof d.footer.description === 'string') setDesignFooterDescription(d.footer.description);
          if (d.footer.contact) {
            if (typeof d.footer.contact.phone === 'string') setDesignFooterPhone(d.footer.contact.phone);
            if (typeof d.footer.contact.email === 'string') setDesignFooterEmail(d.footer.contact.email);
            if (typeof d.footer.contact.hours === 'string') setDesignFooterHours(d.footer.contact.hours);
          }
          if (d.footer.socialLinks) {
             if (typeof d.footer.socialLinks.facebook === 'string') setDesignFooterFacebook(d.footer.socialLinks.facebook);
             if (typeof d.footer.socialLinks.instagram === 'string') setDesignFooterInstagram(d.footer.socialLinks.instagram);
             if (typeof d.footer.socialLinks.whatsapp === 'string') setDesignFooterWhatsapp(d.footer.socialLinks.whatsapp);
          }
        }
      }
    }, (err) => {
      console.warn('[Admin] Error diseño (settings/design) realtime', err);
      setDesignError('No se pudo cargar el diseño');
    });
    // Pedidos en tiempo real (collectionGroup) con degradación si falta índice
    let unsubOrders: (() => void) | null = null;
    let unsubOrdersFallback: (() => void) | null = null;
    const q = query(collectionGroup(db, 'orders'), orderBy('createdAt', 'desc'));
    unsubOrders = onSnapshot(
      q,
      (snap) => {
        setOrders(snap.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() })));
        setOrdersError('');
        setOrdersNotice('');
        setUseOrdersClientFallback(false);
      },
      (err) => {
        console.warn('[Admin] Error pedidos realtime', err);
        // Si falta el índice (failed-precondition), degradar a suscripción sin ordenar
        if ((err as any)?.code === 'failed-precondition') {
          setOrdersError('');
          setOrdersNotice('Mostrando pedidos sin ordenar por falta de índice de createdAt.');
          try {
            unsubOrdersFallback = onSnapshot(
              collectionGroup(db, 'orders'),
              (snap2) => {
                setOrders(snap2.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() })));
              },
              (err2) => {
                console.warn('[Admin] Error fallback pedidos realtime', err2);
                setOrdersError('No se pudieron cargar los pedidos en tiempo real');
                setOrdersNotice('');
              }
            );
          } catch (e) {
            console.warn('[Admin] No se pudo iniciar fallback de pedidos', e);
            setOrdersError('No se pudieron cargar los pedidos en tiempo real');
            setOrdersNotice('');
          }
        } else {
          setOrdersError('No se pudieron cargar los pedidos en tiempo real');
          setUseOrdersClientFallback(true);
        }
      }
    );

    return () => {
      unsubCats();
      unsubProds();
      unsubZones();
      unsubClabe();
      unsubRecipients();
      unsubDesign();
      if (unsubOrders) unsubOrders();
      if (unsubOrdersFallback) unsubOrdersFallback();
      unsubClients();
    };
  }, [isAdmin]);

  // Carga inicial de pedidos (consulta puntual) para evitar esperar interacción
  useEffect(() => {
    if (!isAdmin) return;
    if (orders.length > 0) return;
    const loadInitialOrders = async () => {
      try {
        const q = query(collectionGroup(db, 'orders'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        setOrders(snap.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() })));
        setOrdersError('');
        setOrdersNotice('');
      } catch (e: any) {
        // Si falta el índice de createdAt, degradar a consulta sin ordenar
        if (e?.code === 'failed-precondition') {
          try {
            const snap2 = await getDocs(collectionGroup(db, 'orders'));
            setOrders(snap2.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() })));
            setOrdersError('');
            setOrdersNotice('Mostrando pedidos sin ordenar por falta de índice de createdAt.');
          } catch (e2) {
            setOrdersError('No se pudieron cargar los pedidos en tiempo real');
            setOrdersNotice('');
          }
        } else {
          setOrdersError('No se pudieron cargar los pedidos en tiempo real');
          setOrdersNotice('');
          setUseOrdersClientFallback(true);
        }
      }
    };
    loadInitialOrders();
  }, [isAdmin, orders.length]);

  // Fallback: suscribir pedidos por usuario si la lectura por collectionGroup falla por reglas
  useEffect(() => {
    if (!isAdmin) return;
    // Limpiar suscripciones previas
    const cleanup = () => {
      ordersAggUnsubsRef.current.forEach((u) => {
        try { u(); } catch {}
      });
      ordersAggUnsubsRef.current = [];
      ordersAggBufferRef.current = {};
    };
    if (!useOrdersClientFallback) {
      cleanup();
      return;
    }
    // Iniciar suscripciones por cada cliente
    cleanup();
    clients.forEach((c) => {
      try {
        const q = query(collection(db, 'users', c.id, 'orders'), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(
          q,
          (snap) => {
            ordersAggBufferRef.current[c.id] = snap.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() }));
            const merged = Object.values(ordersAggBufferRef.current).flat();
            merged.sort((a: any, b: any) => {
              const ta = typeof a.createdAt?.toMillis === 'function' ? a.createdAt.toMillis() : (typeof a.createdAt === 'number' ? a.createdAt : 0);
              const tb = typeof b.createdAt?.toMillis === 'function' ? b.createdAt.toMillis() : (typeof b.createdAt === 'number' ? b.createdAt : 0);
              return tb - ta;
            });
            setOrders(merged);
            setOrdersError('');
            setOrdersNotice('Pedidos cargados por usuario (fallback).');
          },
          (err) => {
            console.warn('[Admin] Error suscribiendo pedidos de usuario', c.id, err);
          }
        );
        ordersAggUnsubsRef.current.push(unsub);
      } catch (e) {
        console.warn('[Admin] No se pudo suscribir pedidos para', c.id, e);
      }
    });
    // Cleanup
    return () => cleanup();
  }, [isAdmin, clients, useOrdersClientFallback]);

  // Al cambiar la lista de clientes, obtener conteos de pedidos por usuario (consulta puntual)
  useEffect(() => {
    if (!isAdmin || !clients.length) {
      setOrderCountsByUser({});
      return;
    }
    (async () => {
      try {
        const entries = await Promise.all(
          clients.map(async (c) => {
            const uid = c.id;
            try {
              const snap = await getDocs(collection(db, 'users', uid, 'orders'));
              return [uid, snap.size] as [string, number];
            } catch (e) {
              console.warn('[Admin] Error contando pedidos para', uid, e);
              return [uid, 0] as [string, number];
            }
          })
        );
        const map: Record<string, number> = {};
        entries.forEach(([uid, count]) => {
          map[uid] = count;
        });
        setOrderCountsByUser(map);
      } catch (e) {
        console.warn('[Admin] Error en conteo de pedidos por cliente', e);
      }
    })();
  }, [isAdmin, clients]);

  // Sincronizar conteo cuando el modal carga pedidos
  useEffect(() => {
    if (!openOrdersClientId) return;
    setOrderCountsByUser((prev) => ({
      ...prev,
      [openOrdersClientId]: Array.isArray(ordersModalList) ? ordersModalList.length : 0,
    }));
  }, [openOrdersClientId, ordersModalList]);

  // Cargar pedidos del cliente al abrir el modal (fallback por cliente)
  useEffect(() => {
    // Al cerrar modal, limpiar suscripción puntual
    if (!openOrdersClientId) {
      if (ordersModalUnsubRef.current) {
        try { ordersModalUnsubRef.current(); } catch {}
        ordersModalUnsubRef.current = null;
      }
      setOrdersModalList([]);
      return;
    }
    try {
      const col = collection(db, 'users', openOrdersClientId, 'orders');
      const q = query(col, orderBy('createdAt', 'desc'));
      const unsub = onSnapshot(
        q,
        (snap) => {
          setOrdersModalList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        },
        (err) => {
          console.warn('[Admin] Error cargando pedidos del cliente', openOrdersClientId, err);
          setOrdersModalList([]);
        }
      );
      ordersModalUnsubRef.current = unsub;
    } catch (e) {
      console.warn('[Admin] No se pudo iniciar carga de pedidos del cliente', openOrdersClientId, e);
      setOrdersModalList([]);
    }
    return () => {
      if (ordersModalUnsubRef.current) {
        try { ordersModalUnsubRef.current(); } catch {}
        ordersModalUnsubRef.current = null;
      }
    };
  }, [openOrdersClientId]);

  const handleLogin = async () => {
    setLoginError('');
    try {
      await login(loginEmail, loginPassword);
    } catch (e: any) {
      setLoginError(e.message || 'Error al iniciar sesión');
    }
  };

  const handleAddCategory = async () => {
    setCatError('');
    setCatLoading(true);
    try {
      const name = catName.trim();
      const slug = (catSlug || catName).trim().toLowerCase().replace(/\s+/g, '-');
      if (!name) {
        setCatError('Nombre de categoría requerido');
        setCatLoading(false);
        return;
      }

      let finalImageUrl = catImageUrl;

      if (catImageFile) {
        if (!storage) {
          setCatError('Firebase Storage no configurado');
          setCatLoading(false);
          return;
        }
        const path = `categories/${Date.now()}_${catImageFile.name}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, catImageFile);
        finalImageUrl = await getDownloadURL(fileRef);
      }

      await addDoc(collection(db, 'categories'), { 
        name, 
        slug, 
        image: finalImageUrl,
        active: true,
        createdBy: user?.id || null,
        order: categories.length
      });
      setCatName('');
      setCatSlug('');
      setCatImageUrl('');
      setCatImageFile(null);
    } catch (e) {
      console.error(e);
      setCatError('Error al agregar la categoría');
    } finally {
      setCatLoading(false);
    }
  };

  const handleMoveCategory = async (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === categories.length - 1)) return;

    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap in local array
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    
    setCategories(newCategories);

    try {
      // Update all categories with their new index to ensure consistency
      const updates = newCategories.map((cat, idx) => {
        return updateDoc(doc(db, 'categories', cat.id), { order: idx });
      });
      await Promise.all(updates);
    } catch (error) {
      console.error("Error reordering categories", error);
      setCatError("Error al reordenar categorías");
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'categories', id));
    } catch (e) {
      setCatError('Error al eliminar la categoría');
    }
  };

  const handleToggleCategoryVisibility = async (c: any) => {
    try {
      const current = c.active !== false;
      await updateDoc(doc(db, 'categories', c.id), { active: !current });
    } catch (e) {
      console.error('Error toggling category visibility', e);
      setCatError('Error al cambiar visibilidad de categoría');
    }
  };

  const startEditCategory = (c: any) => {
    setEditingCategoryId(c.id);
    setEditCategoryForm({ name: c.name || '', slug: c.slug || '', imageUrl: c.image || '' });
    setEditCategoryImageFile(null);
  };

  const cancelEditCategory = () => {
    setEditingCategoryId('');
    setEditCategoryForm({ name: '', slug: '', imageUrl: '' });
    setEditCategoryImageFile(null);
  };

  const saveEditCategory = async () => {
    setCatError('');
    setCatLoading(true);
    try {
      if (!editingCategoryId) return;
      const name = editCategoryForm.name.trim();
      const slug = (editCategoryForm.slug || name).trim().toLowerCase().replace(/\s+/g, '-');
      if (!name) {
        setCatError('Nombre de categoría requerido');
        setCatLoading(false);
        return;
      }

      let finalImageUrl = editCategoryForm.imageUrl;

      if (editCategoryImageFile) {
        if (!storage) {
          setCatError('Firebase Storage no configurado');
          setCatLoading(false);
          return;
        }
        const path = `categories/${editingCategoryId}/${Date.now()}_${editCategoryImageFile.name}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, editCategoryImageFile);
        finalImageUrl = await getDownloadURL(fileRef);
      }

      await updateDoc(doc(db, 'categories', editingCategoryId), { name, slug, image: finalImageUrl });
      cancelEditCategory();
    } catch (e) {
      console.error(e);
      setCatError('Error al editar la categoría');
    } finally {
      setCatLoading(false);
    }
  };

  const handleAddProduct = async () => {
    setProdError('');
    try {
      const priceNum = Number(prodForm.price);
      if (!prodForm.name || isNaN(priceNum)) {
        setProdError('Nombre y precio válidos son requeridos');
        return;
      }

      let finalStock = 0;
      if (prodHasVariant) {
        finalStock = prodVariants.reduce((acc, v) => acc + (Number(v.quantity) || 0), 0);
      } else {
        finalStock = Number(prodForm.stock) || 0;
      }

      await addDoc(collection(db, 'products'), {
        name: prodForm.name,
        description: prodForm.description,
        price: priceNum,
        categoryId: prodForm.categoryId || null,
        imageUrl: prodForm.imageUrl || '',
        images: [],
        variants: prodHasVariant ? prodVariants : [],
        stock: finalStock,
        discount: prodDiscount,
        createdBy: user?.id || null,
        createdAt: Date.now(),
      });
      setProdForm({ name: '', description: '', price: '', categoryId: '', imageUrl: '', stock: '' });
      setProdVariants([]);
      setProdDiscount({ active: false, type: 'quantity', discountType: 'percentage', discountValue: 0, limitCount: 0, soldCount: 0 });
      setProdVarQty('');
      setProdVarPrice('');
      setProdHasVariant(false);
      setProdMainVarQty('');
    } catch (e) {
      setProdError('Error al agregar el producto');
    }
  };

  const startEditProduct = (p: any) => {
    setEditingProductId(p.id);
    setEditProductForm({
      name: p.name || '',
      description: p.description || '',
      price: (p.price ?? '').toString(),
      categoryId: p.categoryId || '',
      imageUrl: p.imageUrl || '',
      stock: (p.stock ?? '').toString(),
    });
    setEditVariants(Array.isArray(p.variants) ? p.variants : []);
    setEditHasVariant(Array.isArray(p.variants) && p.variants.length > 0);
    if (p.discount) {
        setEditDiscount(p.discount);
    } else {
        setEditDiscount({ active: false, type: 'quantity', discountType: 'percentage', discountValue: 0, limitCount: 0, soldCount: 0 });
    }
    setEditMainVarQty(
      Array.isArray(p.variants) && p.variants.length > 0 && typeof p.variants[0]?.quantity === 'number'
        ? String(p.variants[0].quantity)
        : ''
    );
  };

  const cancelEditProduct = () => {
    setEditingProductId('');
    setEditProductForm({ name: '', description: '', price: '', categoryId: '', imageUrl: '', stock: '' });
    setEditVariants([]);
    setEditDiscount({ active: false, type: 'quantity', discountType: 'percentage', discountValue: 0, limitCount: 0, soldCount: 0 });
    setEditVarQty('');
    setEditVarPrice('');
    setEditHasVariant(false);
    setEditMainVarQty('');
  };

  const saveEditProduct = async () => {
    setProdError('');
    try {
      if (!editingProductId) return;
      const priceNum = Number(editProductForm.price);
      if (!editProductForm.name || isNaN(priceNum)) {
        setProdError('Nombre y precio válidos son requeridos');
        return;
      }

      let finalStock = 0;
      if (editHasVariant) {
        finalStock = editVariants.reduce((acc, v) => acc + (Number(v.quantity) || 0), 0);
      } else {
        finalStock = Number(editProductForm.stock) || 0;
      }

      await updateDoc(doc(db, 'products', editingProductId), {
        name: editProductForm.name,
        description: editProductForm.description,
        price: priceNum,
        categoryId: editProductForm.categoryId || null,
        imageUrl: editProductForm.imageUrl || '',
        variants: editHasVariant ? editVariants : [],
        stock: finalStock,
        discount: editDiscount,
      });
      cancelEditProduct();
    } catch (e) {
      setProdError('Error al editar el producto');
    }
  };

  // Handlers para variantes (crear)
  const addProdVariant = () => {
    setProdError('');
    const q = Number(prodVarQty);
    const p = Number(prodVarPrice);
    if (isNaN(q) || q <= 0 || isNaN(p)) {
      setProdError('Cantidad (>0) y precio de la variante válidos son requeridos');
      return;
    }
    setProdVariants((prev) => {
      // La primera variante usa el precio del producto principal
      if (!prev.length) {
        const basePrice = Number(prodForm.price);
        return [{ quantity: q, price: isNaN(basePrice) ? p : basePrice }];
      }
      return [...prev, { quantity: q, price: p }];
    });
    setProdVarQty('');
    setProdVarPrice('');
  };

  const removeProdVariant = (index: number) => {
    setProdVariants((prev) => prev.filter((_, i) => i !== index));
  };

  // Handlers para variantes (editar)
  const addEditVariant = () => {
    setProdError('');
    const q = Number(editVarQty);
    const p = Number(editVarPrice);
    if (isNaN(q) || q <= 0 || isNaN(p)) {
      setProdError('Cantidad (>0) y precio de la variante válidos son requeridos');
      return;
    }
    setEditVariants((prev) => {
      // La primera variante usa el precio del producto principal
      if (!prev.length) {
        const basePrice = Number(editProductForm.price);
        return [{ quantity: q, price: isNaN(basePrice) ? p : basePrice }];
      }
      return [...prev, { quantity: q, price: p }];
    });
    setEditVarQty('');
    setEditVarPrice('');
  };

  const removeEditVariant = (index: number) => {
    setEditVariants((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEditVariantField = (index: number, field: 'quantity' | 'price', value: string) => {
    setEditVariants((prev) => {
      const next = [...prev];
      const num = Number(value);
      next[index] = { ...next[index], [field]: isNaN(num) ? 0 : num } as { quantity: number; price: number };
      return next;
    });
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch (e) {
      setProdError('Error al eliminar el producto');
    }
  };

  const handleToggleProductVisibility = async (p: any) => {
    try {
      const current = p.visible !== false;
      await updateDoc(doc(db, 'products', p.id), { visible: !current });
    } catch (e) {
      console.error('Error toggling visibility', e);
      setProdError('Error al cambiar visibilidad');
    }
  };

  const handleAttachImage = async () => {
    setImgError('');
    try {
      if (!imgProductId || !imgUrl) {
        setImgError('Selecciona producto y URL de imagen');
        return;
      }
      await updateDoc(doc(db, 'products', imgProductId), { images: arrayUnion(imgUrl) });
      setImgUrl('');
    } catch (e) {
      setImgError('Error al adjuntar imagen');
    }
  };

  const handleUploadImageFile = async () => {
    setImgError('');
    if (!imgProductId || !imgFile) {
      setImgError('Selecciona producto y archivo de imagen');
      return;
    }
    try {
      setImgUploadLoading(true);
      if (!storage) {
        setImgError('Firebase Storage no está configurado. Verifica REACT_APP_FIREBASE_STORAGE_BUCKET o projectId.');
        return;
      }
      if (!imgFile.type || !imgFile.type.startsWith('image/')) {
        setImgError('El archivo debe ser una imagen válida (JPEG/PNG/WebP).');
        return;
      }
      const path = `products/${imgProductId}/${Date.now()}_${imgFile.name}`;
      const fileRef = storageRef(storage, path);
      // Subir con progreso y timeout para evitar bloqueos prolongados
      const task = uploadBytesResumable(fileRef, imgFile, { contentType: imgFile.type });
      await new Promise<void>((resolve, reject) => {
        const timeoutMs = 30000;
        const timer = setTimeout(() => {
          try { task.cancel(); } catch {}
          reject(new Error(`timeout after ${timeoutMs}ms uploading`));
        }, timeoutMs);
        task.on('state_changed', undefined, (err) => { clearTimeout(timer); reject(err); }, () => { clearTimeout(timer); resolve(); });
      });
      const url = await getDownloadURL(fileRef);
      await updateDoc(doc(db, 'products', imgProductId), {
        imageUrl: url,
        images: arrayUnion(url),
      });
      setImgFile(null);
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      const msg = e?.message || String(e);
      setImgError(`Error al subir imagen (${code}). ${msg.includes('timeout') ? 'Tiempo excedido: revisa conexión/red o proxies.' : (msg.includes('storage') ? 'Revisa las reglas de Storage y permisos del usuario.' : '')}`);
    } finally {
      setImgUploadLoading(false);
    }
  };

  const handleEditImageUrl = async () => {
    setImgError('');
    if (!imgProductId || !editImgExistingUrl || !editImgNewUrl) {
      setImgError('Selecciona producto, imagen y captura la nueva URL');
      return;
    }
    try {
      setEditImgLoading(true);
      const productRef = doc(db, 'products', imgProductId);
      const snap = await getDoc(productRef);
      const data: any = snap.exists() ? snap.data() : {};
      const images: string[] = Array.isArray(data.images) ? data.images : [];
      const newImages = images.map((u) => (u === editImgExistingUrl ? editImgNewUrl : u));
      const payload: any = { images: newImages };
      if (editImgSetAsMain) payload.imageUrl = editImgNewUrl;
      await updateDoc(productRef, payload);
      setEditImgNewUrl('');
      setEditImgExistingUrl('');
      setEditImgSetAsMain(false);
    } catch (e) {
      setImgError('Error al editar la imagen');
    } finally {
      setEditImgLoading(false);
    }
  };

  const handleReplaceImageFile = async () => {
    setImgError('');
    if (!imgProductId || !editImgExistingUrl || !editImgFile) {
      setImgError('Selecciona producto, imagen a reemplazar y archivo');
      return;
    }
    try {
      setEditImgLoading(true);
      if (!storage) {
        setImgError('Firebase Storage no está configurado. Verifica REACT_APP_FIREBASE_STORAGE_BUCKET o projectId.');
        return;
      }
      if (!editImgFile.type || !editImgFile.type.startsWith('image/')) {
        setImgError('El archivo debe ser una imagen válida (JPEG/PNG/WebP).');
        return;
      }
      const path = `products/${imgProductId}/${Date.now()}_${editImgFile.name}`;
      const fileRef = storageRef(storage, path);
      const task = uploadBytesResumable(fileRef, editImgFile, { contentType: editImgFile.type });
      await new Promise<void>((resolve, reject) => {
        const timeoutMs = 30000;
        const timer = setTimeout(() => {
          try { task.cancel(); } catch {}
          reject(new Error(`timeout after ${timeoutMs}ms uploading`));
        }, timeoutMs);
        task.on('state_changed', undefined, (err) => { clearTimeout(timer); reject(err); }, () => { clearTimeout(timer); resolve(); });
      });
      const url = await getDownloadURL(fileRef);
      const productRef = doc(db, 'products', imgProductId);
      const snap = await getDoc(productRef);
      const data: any = snap.exists() ? snap.data() : {};
      const images: string[] = Array.isArray(data.images) ? data.images : [];
      const newImages = images.map((u) => (u === editImgExistingUrl ? url : u));
      const payload: any = { images: newImages };
      if (editImgSetAsMain || !data.imageUrl || data.imageUrl === editImgExistingUrl) payload.imageUrl = url;
      await updateDoc(productRef, payload);
      setEditImgFile(null);
      setEditImgExistingUrl('');
      setEditImgSetAsMain(false);
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      setImgError(`Error al reemplazar imagen (${code}). Revisa reglas de Storage si persiste.`);
    } finally {
      setEditImgLoading(false);
    }
  };

  const handleDeleteProductImage = async (productId: string, url: string) => {
    setImgError('');
    try {
      if (!productId || !url) {
        setImgError('Selecciona producto e imagen para eliminar');
        return;
      }
      const productRef = doc(db, 'products', productId);
      const snap = await getDoc(productRef);
      const data: any = snap.exists() ? snap.data() : {};
      const images: string[] = Array.isArray(data.images) ? data.images : [];
      const newImages = images.filter((u) => u !== url);
      const payload: any = { images: newImages };
      if (data.imageUrl === url) {
        payload.imageUrl = newImages[0] || '';
      }
      await updateDoc(productRef, payload);
    } catch (e) {
      setImgError('Error al eliminar la imagen');
    }
  };

  const setProductMainImage = async (productId: string, url: string) => {
    setImgError('');
    try {
      if (!productId || !url) {
        setImgError('Selecciona producto e imagen válida para establecer como principal');
        return;
      }
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, { imageUrl: url });
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      setImgError(`Error al establecer imagen principal (${code}).`);
    }
  };

  const loadStorageImagesForProduct = async () => {
    setStorageListError('');
    setStorageImages([]);
    setStorageLoading(true);
    const timeout = setTimeout(() => {
      setStorageListError('La carga está tardando demasiado. Intenta de nuevo.');
      setStorageLoading(false);
    }, 15000);
    try {
      if (!storage) {
        setStorageListError('Firebase Storage no está configurado. Verifica REACT_APP_FIREBASE_STORAGE_BUCKET o projectId.');
        clearTimeout(timeout);
        setStorageLoading(false);
        return;
      }
      if (!imgProductId) {
        setStorageListError('Selecciona un producto para listar sus imágenes en el servidor.');
        clearTimeout(timeout);
        setStorageLoading(false);
        return;
      }
      const dirRef = storageRef(storage, `products/${imgProductId}/`);
      // Página inicial para evitar cargas muy grandes
      const res = await list(dirRef, { maxResults: 200 });
      const settled = await Promise.allSettled(res.items.map((itemRef) => getDownloadURL(itemRef)));
      const urls = settled.filter((s) => s.status === 'fulfilled').map((s: any) => s.value as string);
      setStorageImages(urls);
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      const msg = e?.message || String(e);
      // Fallback: intenta listAll si list falla por argumentos
      try {
        if (storage && imgProductId) {
          const dirRef = storageRef(storage, `products/${imgProductId}/`);
          const resAll = await listAll(dirRef);
          const settledAll = await Promise.allSettled(resAll.items.map((itemRef) => getDownloadURL(itemRef)));
          const urlsAll = settledAll.filter((s) => s.status === 'fulfilled').map((s: any) => s.value as string);
          setStorageImages(urlsAll);
          setStorageListError('');
        } else {
          setStorageListError(`Error al listar imágenes (${code}). ${msg}`);
        }
      } catch (e2: any) {
        const code2 = e2?.code || 'desconocido';
        const msg2 = e2?.message || String(e2);
        setStorageListError(`Error al listar imágenes (${code}). ${msg}. Fallback listAll falló (${code2}).`);
      }
    } finally {
      clearTimeout(timeout);
      setStorageLoading(false);
    }
  };

  const attachStorageImageToProduct = async (url: string, setAsMain?: boolean) => {
    setImgError('');
    try {
      if (!imgProductId || !url) {
        setImgError('Selecciona un producto y una imagen válida.');
        return;
      }
      const payload: any = { images: arrayUnion(url) };
      if (setAsMain) payload.imageUrl = url;
      await updateDoc(doc(db, 'products', imgProductId), payload);
    } catch (e) {
      setImgError('Error al adjuntar imagen desde el servidor');
    }
  };

  const updateOrderStatus = async (order: any, nextStatus: string) => {
    try {
      if (!order?.ref) return;
      await updateDoc(order.ref, { status: nextStatus });

      // Notificaciones Push automáticas
      try {
        const lowerStatus = nextStatus.toLowerCase();
        let title = '';
        let body = '';

        if (['enviado', 'shipped'].includes(lowerStatus)) {
          title = 'Pedido Enviado 🚚';
          body = `Tu pedido #${order.id} ha sido enviado y está en camino.`;
        } else if (['en reparto', 'out for delivery'].includes(lowerStatus)) {
          title = 'En Reparto 🛵';
          body = `¡Tu pedido #${order.id} está saliendo a entrega! Prepárate para recibirlo.`;
        } else if (['entregado', 'delivered'].includes(lowerStatus)) {
          title = 'Pedido Entregado 🎁';
          body = `Tu pedido #${order.id} ha sido entregado. ¡Esperamos que sea una gran sorpresa!`;
        }

        if (title && order.userId) {
          await addDoc(collection(db, 'pushQueue'), {
            title,
            body,
            userId: order.userId,
            target: 'userId',
            status: 'pending',
            createdAt: serverTimestamp(),
            deepLink: 'blum://orders'
          });
        }
      } catch (pushErr) {
        console.warn('[Admin] Error encolando notificación push', pushErr);
      }

      // Enviar correo de "Entregado" si corresponde
      if (['entregado', 'delivered'].includes(nextStatus.toLowerCase())) {
        try {
          const email = order.userEmail || order.sender?.email;
          const name = order.userName || order.sender?.name;
          if (email) {
            fetch('/api/orders/delivered', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email,
                name,
                orderId: order.id,
                items: order.items,
                total: order.total,
                shippingCost: order.shippingCost || 0,
                shipping: order.shipping || {}
              }),
            }).catch(err => console.warn('[Admin] Error enviando email de entrega', err));
          }
        } catch (mailErr) {
          console.warn('[Admin] Error preparando email de entrega', mailErr);
        }
      }
    } catch (e) {
      console.warn('[Admin] Error actualizando estado', e);
    }
  };

  // Estado y helpers para cupones
  const [coupons, setCoupons] = useState<any[]>([]);
  const [couponError, setCouponError] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponType, setCouponType] = useState<'value' | 'percentage' | 'free_shipping'>('value');
  const [couponAmount, setCouponAmount] = useState<string>('');
  const [couponActive, setCouponActive] = useState<boolean>(true);
  const [couponDesc, setCouponDesc] = useState<string>('');

  useEffect(() => {
    // Suscripción a cupones (solo admin)
    try {
      if (!isAdmin) return;
      const q = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
      const unsub = onSnapshot(q, (snap) => {
        const list: any[] = [];
        snap.forEach((d) => list.push({ id: d.id, ref: d.ref, ...d.data() }));
        setCoupons(list);
      }, (err) => {
        console.warn('[Admin] Error suscripción cupones', err);
        setCouponError('No se pudieron cargar los cupones');
      });
      return () => unsub();
    } catch (e) {
      console.warn('[Admin] Error iniciando cupones', e);
    }
  }, [isAdmin]);

  const handleAddCoupon = async () => {
    setCouponError('');
    try {
      const amountNum = Number(couponAmount);
      if (!couponCode.trim()) { setCouponError('Código es requerido'); return; }
      
      if (couponType !== 'free_shipping' && (isNaN(amountNum) || amountNum <= 0)) { 
        setCouponError('Monto válido (> 0) es requerido'); 
        return; 
      }
      
      const payload: any = {
        code: couponCode.trim(),
        type: couponType, // 'value', 'percentage' o 'free_shipping'
        amount: couponType === 'free_shipping' ? 0 : amountNum,
        active: couponActive,
        description: couponDesc || '',
        createdAt: Date.now(),
      };
      await addDoc(collection(db, 'coupons'), payload);
      setCouponCode('');
      setCouponAmount('');
      setCouponActive(true);
      setCouponDesc('');
      setCouponType('value');
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      setCouponError(`Error al crear cupón (${code}).`);
    }
  };

  const toggleCouponActive = async (c: any) => {
    try {
      if (!c?.ref) return;
      await updateDoc(c.ref, { active: !c.active });
    } catch (e) {
      console.warn('[Admin] Error actualizando cupón', e);
    }
  };

  const deleteCoupon = async (c: any) => {
    try {
      if (!c?.ref) return;
      await deleteDoc(c.ref);
    } catch (e) {
      console.warn('[Admin] Error eliminando cupón', e);
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('paid') || s.includes('pagado')) return { bg: '#e3f2fd', color: '#1976d2', label: 'Pagado' };
    if (s.includes('preparación') || s.includes('preparation')) return { bg: '#fff3e0', color: '#f57c00', label: 'En preparación' };
    if (s.includes('enviado') || s.includes('shipped')) return { bg: '#f3e5f5', color: '#7b1fa2', label: 'Enviado' };
    if (s.includes('entregado') || s.includes('delivered')) return { bg: '#e8f5e9', color: '#388e3c', label: 'Entregado' };
    return { bg: '#f5f5f5', color: '#757575', label: status || 'Pendiente' };
  };

  const handleSaveZones = async () => {
    setZoneError('');
    setZoneSuccess('');
    try {
      const cities = zoneCities.split(',').map((c) => c.trim()).filter(Boolean);
      const zipCodes = zoneZipCodes.split(',').map((z) => z.trim()).filter(Boolean);
      const shippingCostNum = Number(zoneShippingCost);
      if (isNaN(shippingCostNum) || shippingCostNum < 0) {
        setZoneError('Costo de envío válido (>= 0) es requerido');
        return;
      }
      await setDoc(doc(db, 'settings', 'deliveryZones'), {
        // Modelo anterior para compatibilidad
        cities,
        zipCodes,
        shippingCost: shippingCostNum,
        // Nuevo modelo: ciudad bloqueada y matriz por CP
        city: zoneCityLocked,
        zipCodeEntries: zipCodeEntries.map((e) => ({
          zipCode: e.zipCode,
          colonias: e.colonias,
          cost: e.cost,
        })),
        updatedBy: user?.id || null,
        updatedAt: Date.now(),
      });
      setZoneSuccess('Zonas de reparto actualizadas');
    } catch (e) {
      setZoneError('Error al guardar zonas de reparto');
    }
  };

  const handleSaveBankDetails = async () => {
    setClabeError('');
    setClabeSuccess('');
    setClabeLoading(true);
    try {
      await setDoc(doc(db, 'settings', 'clabe'), {
        clabe: clabe.trim(),
        bankName: bankName.trim(),
        beneficiary: beneficiary.trim(),
        updatedBy: user?.id || null,
        updatedAt: Date.now(),
      });
      setClabeSuccess('Datos bancarios actualizados correctamente');
    } catch (e) {
      setClabeError('Error al guardar los datos bancarios');
    } finally {
      setClabeLoading(false);
    }
  };

  // Validaciones simples para diseño
  const validateHexColor = (value: string) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
  const validateUrl = (value: string) => /^(https?:\/\/|gs:\/\/).+/.test((value || '').trim());

  const handleSaveDesign = async () => {
    setDesignError('');
    setDesignSuccess('');
    try {
      const primary = designPrimaryColor.trim();
      const accent = designAccentColor.trim();
      const footer = designFooterColor.trim();
      const logo = designLogoColor.trim();
      const button = (designButtonColor || '').trim();
      const header = (designHeaderColor || '').trim();
      const servicesColor = (designServicesSectionColor || '').trim();

      if (!validateHexColor(primary) || !validateHexColor(accent)) {
        setDesignError('Colores deben ser hex válidos, ej. #e91e63');
        return;
      }
      if (!validateHexColor(footer) || !validateHexColor(logo)) {
        setDesignError('Footer y logos deben ser hex válidos, ej. #121212, #ffffff');
        return;
      }
      if (button && !validateHexColor(button)) {
        setDesignError('Color de botones debe ser hex válido, ej. #e91e63');
        return;
      }
      if (header && !validateHexColor(header)) {
        setDesignError('Color del header debe ser hex válido, ej. #a733ff');
        return;
      }
      if (servicesColor && !validateHexColor(servicesColor)) {
        setDesignError('Color de sección servicios debe ser hex válido, ej. #a733ff');
        return;
      }

      const payload: any = {
        primaryColor: primary,
        accentColor: accent,
        footerColor: footer,
        logoColor: logo,
        heroTitle: designHeroTitle.trim(),
        heroSubtitle: designHeroSubtitle.trim(),
        homeImages: designHomeImages,
        updatedBy: user?.id || null,
        updatedAt: Date.now(),
        headerColor: header,
        servicesSectionColor: servicesColor,
        supportPhone: designSupportPhone.trim(),
        servicesContent: designServicesContent,
        aboutUs: {
          title: designAboutTitle,
          description: designAboutDescription,
          imageUrl: designAboutImageUrl
        },
        footer: {
          description: designFooterDescription,
          contact: {
            phone: designFooterPhone,
            email: designFooterEmail,
            hours: designFooterHours
          },
          socialLinks: {
            facebook: designFooterFacebook,
            instagram: designFooterInstagram,
            whatsapp: designFooterWhatsapp
          }
        }
      };
      if (button) payload.buttonColor = button;
      if (designLogoUrl.trim()) payload.logoUrl = designLogoUrl.trim();
      if (designFaviconUrl.trim()) payload.faviconUrl = designFaviconUrl.trim();
      if (designAllCategoryImageUrl.trim()) payload.allCategoryImageUrl = designAllCategoryImageUrl.trim();
      if (designBannerUrl.trim()) payload.bannerUrl = designBannerUrl.trim();
      if (payload.logoUrl && !validateUrl(payload.logoUrl)) {
        setDesignError('Logo URL debe ser http(s):// o gs:// válido');
        return;
      }
      if (payload.faviconUrl && !validateUrl(payload.faviconUrl)) {
        setDesignError('Favicon URL debe ser http(s):// o gs:// válido');
        return;
      }
      if (payload.bannerUrl && !validateUrl(payload.bannerUrl)) {
        setDesignError('Banner URL debe ser http(s):// o gs:// válido');
        return;
      }
      await setDoc(doc(db, 'settings', 'design'), payload, { merge: true });
      try {
        // Persist locally to ensure immediate theme on next load and avoid initial flash
        const localPayload = {
          primaryColor: payload.primaryColor,
          accentColor: payload.accentColor,
          footerColor: payload.footerColor,
          logoColor: payload.logoColor,
          buttonColor: payload.buttonColor,
          logoUrl: payload.logoUrl,
          faviconUrl: payload.faviconUrl,
          allCategoryImageUrl: payload.allCategoryImageUrl,
          bannerUrl: payload.bannerUrl,
          heroTitle: payload.heroTitle,
          heroSubtitle: payload.heroSubtitle,
          buttonRadius: payload.buttonRadius,
          headerColor: payload.headerColor,
          servicesSectionColor: payload.servicesSectionColor,
          servicesContent: payload.servicesContent,
          aboutUs: payload.aboutUs,
          footer: payload.footer
        };
        window.localStorage.setItem('designConfig', JSON.stringify(localPayload));
      } catch {}
      setDesignSuccess('Diseño actualizado');
    } catch (e) {
      setDesignError('Error al guardar diseño');
    }
  };

  const handleUploadLogoFile = async () => {
    setDesignLogoUploadError('');
    try {
      if (!designLogoFile) {
        setDesignLogoUploadError('Selecciona un archivo de imagen para el logo');
        return;
      }
      if (!storage) {
        setDesignLogoUploadError('Firebase Storage no está configurado. Verifica REACT_APP_FIREBASE_STORAGE_BUCKET o projectId.');
        return;
      }
      if (!designLogoFile.type || !designLogoFile.type.startsWith('image/')) {
        setDesignLogoUploadError('El archivo debe ser una imagen válida (JPEG/PNG/WebP).');
        return;
      }

      // Validar dimensiones
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (img.height < 60 || img.height > 200) {
            reject(new Error('La imagen debe tener una altura entre 60px y 200px para asegurar una buena visualización en el header.'));
          } else {
            resolve();
          }
        };
        img.onerror = () => reject(new Error('No se pudo leer el archivo de imagen.'));
        img.src = URL.createObjectURL(designLogoFile);
      });

      setDesignLogoUploading(true);
      const path = `branding/logo/${Date.now()}_${designLogoFile.name}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, designLogoFile, { contentType: designLogoFile.type });
      const url = await getDownloadURL(fileRef);
      setDesignLogoUrl(url);
      setDesignLogoFile(null);
      setDesignSuccess('Logo subido. Recuerda guardar el diseño para persistir cambios.');
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      const msg = e?.message || String(e);
      setDesignLogoUploadError(`Error al subir logo (${code}). ${msg.includes('storage') ? 'Revisa las reglas de Storage y permisos del usuario.' : ''}`);
    } finally {
      setDesignLogoUploading(false);
    }
  };

  const handleUploadFaviconFile = async () => {
    setDesignFaviconUploadError('');
    try {
      if (!designFaviconFile) {
        setDesignFaviconUploadError('Selecciona un archivo de imagen para el favicon');
        return;
      }
      if (!storage) {
        setDesignFaviconUploadError('Firebase Storage no está configurado. Verifica REACT_APP_FIREBASE_STORAGE_BUCKET o projectId.');
        return;
      }
      if (!designFaviconFile.type || !designFaviconFile.type.startsWith('image/')) {
        setDesignFaviconUploadError('El archivo debe ser una imagen válida.');
        return;
      }
      setDesignFaviconUploading(true);
      const path = `branding/favicon/${Date.now()}_${designFaviconFile.name}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, designFaviconFile, { contentType: designFaviconFile.type });
      const url = await getDownloadURL(fileRef);
      setDesignFaviconUrl(url);
      setDesignFaviconFile(null);
      setDesignSuccess('Favicon subido. Recuerda guardar el diseño para persistir cambios.');
    } catch (e: any) {
      const code = e?.code || 'desconocido';
      const msg = e?.message || String(e);
      setDesignFaviconUploadError(`Error al subir favicon (${code}). ${msg.includes('storage') ? 'Revisa las reglas de Storage y permisos del usuario.' : ''}`);
    } finally {
      setDesignFaviconUploading(false);
    }
  };

  const handleUploadAllCategoryFile = async () => {
    setDesignAllCategoryImageUploadError('');
    try {
      if (!designAllCategoryImageFile) {
        setDesignAllCategoryImageUploadError('Selecciona un archivo de imagen para la categoría Todos');
        return;
      }
      if (!storage) {
        setDesignAllCategoryImageUploadError('Firebase Storage no está configurado.');
        return;
      }
      if (!designAllCategoryImageFile.type || !designAllCategoryImageFile.type.startsWith('image/')) {
        setDesignAllCategoryImageUploadError('El archivo debe ser una imagen válida.');
        return;
      }
      setDesignAllCategoryImageUploading(true);
      const path = `branding/allCategory/${Date.now()}_${designAllCategoryImageFile.name}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, designAllCategoryImageFile, { contentType: designAllCategoryImageFile.type });
      const url = await getDownloadURL(fileRef);
      setDesignAllCategoryImageUrl(url);

      // Guardar automáticamente en Firestore
      if (db) {
        await updateDoc(doc(db, 'settings', 'design'), {
          allCategoryImageUrl: url
        });
      }

      setDesignAllCategoryImageFile(null);
      setDesignSuccess('Imagen de categoría "Todos" actualizada correctamente.');
    } catch (e: any) {
      console.error(e);
      setDesignAllCategoryImageUploadError('Error al subir imagen o guardar configuración.');
    } finally {
      setDesignAllCategoryImageUploading(false);
    }
  };

  const handleUploadAboutImageFile = async () => {
    setDesignAboutImageUploadError('');
    try {
      if (!designAboutImageFile) {
        setDesignAboutImageUploadError('Selecciona un archivo de imagen para Sobre Nosotros');
        return;
      }
      if (!storage) {
        setDesignAboutImageUploadError('Firebase Storage no está configurado.');
        return;
      }
      if (!designAboutImageFile.type || !designAboutImageFile.type.startsWith('image/')) {
        setDesignAboutImageUploadError('El archivo debe ser una imagen válida.');
        return;
      }
      setDesignAboutImageUploading(true);
      const path = `branding/about/${Date.now()}_${designAboutImageFile.name}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, designAboutImageFile, { contentType: designAboutImageFile.type });
      const url = await getDownloadURL(fileRef);
      setDesignAboutImageUrl(url);

      // Guardar automáticamente en Firestore
      if (db) {
        await updateDoc(doc(db, 'settings', 'design'), {
          'aboutUs.imageUrl': url
        });
      }

      setDesignAboutImageFile(null);
      setDesignSuccess('Imagen "Sobre Nosotros" actualizada correctamente.');
    } catch (e: any) {
      console.error(e);
      setDesignAboutImageUploadError('Error al subir imagen o guardar configuración.');
    } finally {
      setDesignAboutImageUploading(false);
    }
  };

  const handleUploadHomeImage = async () => {
    if (!designHomeImageFile) return;
    if (!storage) return;
    try {
      setDesignHomeImageUploading(true);
      const path = `branding/home/${Date.now()}_${designHomeImageFile.name}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, designHomeImageFile, { contentType: designHomeImageFile.type });
      const url = await getDownloadURL(fileRef);
      setDesignHomeImages(prev => [...prev, url]);
      setDesignHomeImageFile(null);
    } catch (e) {
      console.warn('Error uploading home image', e);
      setDesignHomeImageUploadError('Error subiendo imagen');
    } finally {
      setDesignHomeImageUploading(false);
    }
  };

  const handleRemoveHomeImage = (index: number) => {
    setDesignHomeImages(prev => prev.filter((_, i) => i !== index));
  };

  // Estados de acceso
  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
            Acceso al Panel de Administrador
          </Typography>
          {loginError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{loginError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField 
              label="Email" 
              type="email" 
              value={loginEmail} 
              onChange={(e) => setLoginEmail(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
            <TextField 
              label="Password" 
              type="password" 
              value={loginPassword} 
              onChange={(e) => setLoginPassword(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
            <Button 
              variant="contained" 
              onClick={handleLogin} 
              sx={{ 
                backgroundColor: 'primary.main', 
                '&:hover': { backgroundColor: 'primary.dark' },
                borderRadius: '50px',
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Iniciar sesión
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (authChecked && !isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
            Acceso restringido
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: '12px' }}>Tu cuenta no tiene permisos de administrador.</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#333', fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
        Panel de Administrador
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Alert severity={isAdmin ? 'success' : 'warning'} sx={{ borderRadius: '12px' }}>
          Sesión: uid={user?.id || '—'}, email={user?.email || '—'}, isAdmin={String(!!isAdmin)}. authChecked={String(!!authChecked)}. Storage bucket: {process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '(por defecto)'}
        </Alert>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Menú</Typography>
                <List>
                  <ListItemButton selected={activeSection === 'sales'} onClick={() => setActiveSection('sales')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Ventas / Analytics" />
                  </ListItemButton>
                  <ListItemButton 
                    component="a" 
                    href={`https://console.firebase.google.com/project/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/crashlytics`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ borderRadius: '12px', mb: 1 }}
                  >
                    <ListItemText primary="Crashlytics" secondary="Abrir consola externa" />
                  </ListItemButton>
                  <ListItemButton 
                    component="a" 
                    href={`https://console.firebase.google.com/project/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/analytics/reports`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ borderRadius: '12px', mb: 1 }}
                  >
                    <ListItemText primary="Analytics" secondary="Abrir consola externa" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'admin'} onClick={() => setActiveSection('admin')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Menú de Productos / Envío" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'bank_details'} onClick={() => setActiveSection('bank_details')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Datos Bancarios" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'design'} onClick={() => setActiveSection('design')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Diseño" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'clients'} onClick={() => setActiveSection('clients')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Clientes" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'realtime'} onClick={() => setActiveSection('realtime')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Pedidos en tiempo real" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'previous'} onClick={() => setActiveSection('previous')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Pedidos anteriores" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'coupons'} onClick={() => setActiveSection('coupons')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Cupones" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'push'} onClick={() => setActiveSection('push')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Notificaciones Push" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'stripe'} onClick={() => setActiveSection('stripe')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Stripe Key" />
                  </ListItemButton>
                  <ListItemButton selected={activeSection === 'telegram'} onClick={() => setActiveSection('telegram')} sx={{ borderRadius: '12px', mb: 1 }}>
                    <ListItemText primary="Telegram" />
                  </ListItemButton>
                </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          {activeSection === 'sales' && (
             <SalesDashboard orders={orders} />
          )}
          {activeSection === 'admin' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={tab} 
                  onChange={(_, v) => setTab(v)} 
                  variant="scrollable" 
                  scrollButtons="auto"
                  sx={{ '& .MuiTab-root': { textTransform: 'none', fontSize: '1rem', minHeight: '48px' } }}
                >
                  <Tab label="Categorías" />
                  <Tab label="Productos" />
                  <Tab label="Imágenes de los productos" />
                  <Tab label="Zonas de reparto" />
                </Tabs>
              </Box>

              {tab === 0 && (
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: 'text.primary', mb: 3 }}>
                    Categorías
                  </Typography>
                  {catError && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{catError}</Alert>}
                  
                  {/* Sección especial para categoría "Todos" */}
                  <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '24px', bgcolor: '#f0f4f8', border: '1px solid', borderColor: 'primary.main' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalFlorist color="primary" /> Categoría Especial "Todos"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Esta categoría se genera automáticamente y muestra todos los productos. Puedes personalizar su imagen aquí.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box 
                        component="img" 
                        src={designAllCategoryImageUrl || 'https://images.unsplash.com/photo-1490750967868-58cb75069faf?w=150'} 
                        sx={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid', borderColor: 'primary.main', bgcolor: 'white' }} 
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="file"
                            accept="image/*"
                            id="upload-all-cat-input"
                            style={{ display: 'none' }}
                            onChange={(e) => setDesignAllCategoryImageFile(e.target.files ? e.target.files[0] : null)}
                          />
                          <label htmlFor="upload-all-cat-input">
                            <Button variant="outlined" component="span" sx={{ borderRadius: '50px', textTransform: 'none' }}>
                              Seleccionar Imagen
                            </Button>
                          </label>
                          {designAllCategoryImageFile && <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{designAllCategoryImageFile.name}</Typography>}
                          <Button 
                            variant="contained" 
                            disabled={designAllCategoryImageUploading || !designAllCategoryImageFile} 
                            onClick={handleUploadAllCategoryFile} 
                            sx={{ borderRadius: '50px', textTransform: 'none', px: 3 }}
                          >
                            {designAllCategoryImageUploading ? 'Subiendo...' : 'Actualizar Imagen'}
                          </Button>
                        </Box>
                        {designAllCategoryImageUploadError && <Alert severity="error" sx={{ mt: 1, borderRadius: '12px' }}>{designAllCategoryImageUploadError}</Alert>}
                        {designSuccess && designSuccess.includes('Todos') && <Alert severity="success" sx={{ mt: 1, borderRadius: '12px' }}>{designSuccess}</Alert>}
                      </Box>
                    </Box>

                    {/* Precios Especiales (Descuentos) */}
                    <Box sx={{ mb: 4, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: '16px', bgcolor: '#fff' }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CardGiftcard fontSize="small" color="primary" /> Precios Especiales (Mayoreo/Temporada)
                      </Typography>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={prodDiscount.active}
                            onChange={(e) => setProdDiscount({ ...prodDiscount, active: e.target.checked })}
                          />
                        }
                        label="Activar Promoción"
                        sx={{ mb: 2 }}
                      />

                      {prodDiscount.active && (
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <TextField
                              select
                              fullWidth
                              label="Tipo de Límite"
                              value={prodDiscount.type}
                              onChange={(e) => setProdDiscount({ ...prodDiscount, type: e.target.value as any })}
                              size="small"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            >
                              <MenuItem value="quantity">Límite Global (Cantidad)</MenuItem>
                              <MenuItem value="date">Por Tiempo (Fechas)</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              select
                              fullWidth
                              label="Tipo de Descuento"
                              value={prodDiscount.discountType}
                              onChange={(e) => setProdDiscount({ ...prodDiscount, discountType: e.target.value as any })}
                              size="small"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            >
                              <MenuItem value="percentage">Porcentaje (%)</MenuItem>
                              <MenuItem value="fixed">Monto Fijo ($)</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label={prodDiscount.discountType === 'percentage' ? "Porcentaje (%)" : "Monto a Descontar ($)"}
                              type="number"
                              value={prodDiscount.discountValue || ''}
                              onChange={(e) => setProdDiscount({ ...prodDiscount, discountValue: Number(e.target.value) })}
                              size="small"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                          </Grid>

                          {prodDiscount.type === 'quantity' && (
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label="Límite Global de Piezas"
                                type="number"
                                value={prodDiscount.limitCount || ''}
                                onChange={(e) => setProdDiscount({ ...prodDiscount, limitCount: Number(e.target.value) })}
                                helperText={`Vendidos: ${prodDiscount.soldCount || 0}`}
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                              />
                            </Grid>
                          )}

                          {prodDiscount.type === 'date' && (
                            <>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="Inicio"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={prodDiscount.startDate || ''}
                                  onChange={(e) => setProdDiscount({ ...prodDiscount, startDate: e.target.value })}
                                  size="small"
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="Fin"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  value={prodDiscount.endDate || ''}
                                  onChange={(e) => setProdDiscount({ ...prodDiscount, endDate: e.target.value })}
                                  size="small"
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                      )}
                    </Box>
                  </Paper>

                  <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '24px', bgcolor: '#f8f9fa', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Agregar Nueva Categoría</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField 
                          label="Nombre" 
                          value={catName} 
                          onChange={(e) => setCatName(e.target.value)} 
                          fullWidth 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                        <TextField 
                          label="Slug" 
                          value={catSlug} 
                          onChange={(e) => setCatSlug(e.target.value)} 
                          fullWidth 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField 
                          label="Imagen URL" 
                          value={catImageUrl} 
                          onChange={(e) => setCatImageUrl(e.target.value)} 
                          fullWidth 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
                        />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setCatImageFile(e.target.files ? e.target.files[0] : null)}
                        />
                        <Button 
                          variant="contained" 
                          disabled={catLoading} 
                          onClick={handleAddCategory} 
                          sx={{ 
                            borderRadius: '50px', 
                            textTransform: 'none', 
                            backgroundColor: 'primary.main', 
                            '&:hover': { backgroundColor: 'primary.dark' },
                            px: 4,
                            minWidth: 120
                          }}
                        >
                          {catLoading ? 'Guardando...' : 'Agregar'}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>

                  <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {categories.map((c, index) => (
                      <Paper key={c.id} elevation={0} sx={{ borderRadius: '24px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                        <ListItem divider={false} sx={{ flexDirection: 'column', alignItems: 'stretch', p: 0 }}>
                        {editingCategoryId === c.id ? (
                          <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField 
                                  label="Nombre" 
                                  value={editCategoryForm.name} 
                                  onChange={(e) => setEditCategoryForm({ ...editCategoryForm, name: e.target.value })} 
                                  fullWidth
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                                />
                                <TextField 
                                  label="Slug" 
                                  value={editCategoryForm.slug} 
                                  onChange={(e) => setEditCategoryForm({ ...editCategoryForm, slug: e.target.value })} 
                                  fullWidth
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField 
                                  label="Imagen URL" 
                                  value={editCategoryForm.imageUrl} 
                                  onChange={(e) => setEditCategoryForm({ ...editCategoryForm, imageUrl: e.target.value })} 
                                  fullWidth 
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                                />
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => setEditCategoryImageFile(e.target.files ? e.target.files[0] : null)}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button onClick={cancelEditCategory} sx={{ borderRadius: '50px', textTransform: 'none', color: 'text.secondary' }}>Cancelar</Button>
                                <Button 
                                  variant="contained" 
                                  disabled={catLoading} 
                                  onClick={saveEditCategory} 
                                  sx={{ 
                                    borderRadius: '50px', 
                                    textTransform: 'none', 
                                    backgroundColor: 'primary.main', 
                                    '&:hover': { backgroundColor: 'primary.dark' },
                                    px: 3
                                  }}
                                >
                                  {catLoading ? 'Guardando...' : 'Guardar Cambios'}
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', p: 2, width: '100%', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleMoveCategory(index, 'up')} 
                                disabled={index === 0}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleMoveCategory(index, 'down')} 
                                disabled={index === categories.length - 1}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </Box>
                            {c.image ? (
                              <Box component="img" src={c.image} sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '12px', bgcolor: '#f0f0f0' }} />
                            ) : (
                              <Box sx={{ width: 60, height: 60, borderRadius: '12px', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" color="text.secondary">No img</Typography>
                              </Box>
                            )}

                            
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{c.slug}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={c.active !== false}
                                    onChange={() => handleToggleCategoryVisibility(c)}
                                    size="small"
                                  />
                                }
                                label={<Typography variant="body2" color="text.secondary">{c.active === false ? "Inactivo" : "Activo"}</Typography>}
                                sx={{ mr: 1 }}
                              />
                              <Button 
                                onClick={() => startEditCategory(c)} 
                                sx={{ 
                                  minWidth: 'auto', 
                                  borderRadius: '12px', 
                                  textTransform: 'none', 
                                  mr: 1 
                                }}
                              >
                                Editar
                              </Button>
                              <IconButton 
                                color="error" 
                                onClick={() => handleDeleteCategory(c.id)}
                                sx={{ bgcolor: 'rgba(211, 47, 47, 0.04)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        )}
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                </Box>
              )}

              {tab === 1 && (
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: 'text.primary', mb: 3 }}>
                    Gestión de Productos
                  </Typography>
                  {prodError && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{prodError}</Alert>}
                  
                  <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '24px', bgcolor: '#f8f9fa', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Agregar Nuevo Producto</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={4}>
                        <TextField 
                          fullWidth 
                          label="Nombre" 
                          value={prodForm.name} 
                          onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField 
                          fullWidth 
                          label="Descripción" 
                          multiline
                          rows={4}
                          value={prodForm.description} 
                          onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField 
                          fullWidth 
                          label="Precio" 
                          type="number" 
                          value={prodForm.price} 
                          onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      {!prodHasVariant && (
                        <Grid item xs={12} md={2}>
                          <TextField 
                            fullWidth 
                            label="Stock" 
                            type="number" 
                            value={prodForm.stock} 
                            onChange={(e) => setProdForm({ ...prodForm, stock: e.target.value })} 
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} md={prodHasVariant ? 4 : 2}>
                        <TextField 
                          fullWidth 
                          label="Imagen principal (URL)" 
                          value={prodForm.imageUrl} 
                          onChange={(e) => setProdForm({ ...prodForm, imageUrl: e.target.value })} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField 
                          select 
                          fullWidth 
                          label="Categoría" 
                          value={prodForm.categoryId} 
                          onChange={(e) => setProdForm({ ...prodForm, categoryId: e.target.value })}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                          {categories.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button 
                          variant="contained" 
                          onClick={handleAddProduct} 
                          sx={{ 
                            backgroundColor: 'primary.main', 
                            '&:hover': { backgroundColor: 'primary.dark' },
                            borderRadius: '50px',
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          Agregar Producto
                        </Button>
                      </Grid>
                    </Grid>

                    {/* Variantes (opcional) en creación */}
                    <Box sx={{ mb: 1, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Variantes (Opcional)</Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={prodHasVariant}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setProdHasVariant(checked);
                              if (!checked) {
                                setProdVariants([]);
                                setProdVarQty('');
                                setProdVarPrice('');
                                setProdMainVarQty('');
                              }
                            }}
                          />
                        }
                        label="Este producto tiene variantes"
                      />
                      {prodHasVariant && (
                        <Box sx={{ mt: 2 }}>
                          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Cantidad (variante principal)"
                                type="number"
                                value={prodMainVarQty}
                                size="small"
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setProdMainVarQty(val);
                                  setProdVariants((prev) => {
                                    const q = Number(val);
                                    const basePrice = Number(prodForm.price);
                                    if (!prev.length) {
                                      if (isNaN(q) || q <= 0) return prev;
                                      return [{ quantity: q, price: isNaN(basePrice) ? 0 : basePrice }];
                                    }
                                    return [{ ...prev[0], quantity: isNaN(q) ? prev[0].quantity : q }, ...prev.slice(1)];
                                  });
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                            />
                          </Grid>
                        </Grid>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Agregar variantes adicionales:</Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <TextField 
                              fullWidth 
                              label="Cantidad" 
                              type="number" 
                              value={prodVarQty} 
                              onChange={(e) => setProdVarQty(e.target.value)} 
                              size="small"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField 
                              fullWidth 
                              label="Precio variante" 
                              type="number" 
                              value={prodVarPrice} 
                              onChange={(e) => setProdVarPrice(e.target.value)} 
                              size="small"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Button 
                                variant="outlined" 
                                onClick={addProdVariant}
                                sx={{ borderRadius: '50px', textTransform: 'none' }}
                              >
                                Agregar variante
                              </Button>
                            </Grid>
                          </Grid>
                          <List dense sx={{ mt: 2 }}>
                            {prodVariants.map((v, idx) => (
                              <ListItem key={`new-var-${idx}`} sx={{ bgcolor: 'white', borderRadius: '12px', mb: 1, border: '1px solid', borderColor: 'divider' }} secondaryAction={
                                <IconButton edge="end" color="error" onClick={() => removeProdVariant(idx)}>
                                  <DeleteIcon />
                                </IconButton>
                              }>
                                <ListItemText primary={`Cantidad: ${v.quantity} • Precio: ${v.price}${idx === 0 ? ' (precio del producto)' : ''}`} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  </Paper>

                   <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                     {products.map((p) => (
                       <Paper key={p.id} elevation={0} sx={{ borderRadius: '24px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                         <ListItem divider={false} sx={{ flexDirection: 'column', alignItems: 'stretch', p: 0 }}>
                         {editingProductId === p.id ? (
                           <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%', alignItems: 'center', mb: 2 }}>
                               <TextField label="Nombre" value={editProductForm.name} onChange={(e) => setEditProductForm({ ...editProductForm, name: e.target.value })} sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                              <TextField label="Descripción" multiline rows={4} value={editProductForm.description} onChange={(e) => setEditProductForm({ ...editProductForm, description: e.target.value })} sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                              <TextField label="Precio" type="number" value={editProductForm.price} onChange={(e) => setEditProductForm({ ...editProductForm, price: e.target.value })} sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                              {!editHasVariant && (
                                <TextField label="Stock" type="number" value={editProductForm.stock} onChange={(e) => setEditProductForm({ ...editProductForm, stock: e.target.value })} sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                              )}
                              <TextField select label="Categoría" value={editProductForm.categoryId} onChange={(e) => setEditProductForm({ ...editProductForm, categoryId: e.target.value })} sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
                                 {categories.map((c) => (
                                   <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                 ))}
                               </TextField>
                               <TextField label="Imagen principal (URL)" value={editProductForm.imageUrl} onChange={(e) => setEditProductForm({ ...editProductForm, imageUrl: e.target.value })} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} />
                             </Box>

                              {/* Variantes en edición */}
                              <Box sx={{ width: '100%', mt: 2, p: 2, bgcolor: 'white', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Variantes</Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={editHasVariant}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setEditHasVariant(checked);
                                        if (!checked) {
                                          setEditVariants([]);
                                          setEditVarQty('');
                                          setEditVarPrice('');
                                          setEditMainVarQty('');
                                        }
                                      }}
                                    />
                                  }
                                  label="Tiene variante"
                                />
                                {editHasVariant && (
                                  <Box sx={{ mt: 2 }}>
                                    <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                      <Grid item xs={12} md={4}>
                                        <TextField
                                          label="Cantidad (variante principal)"
                                          type="number"
                                          value={editMainVarQty}
                                          size="small"
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            setEditMainVarQty(val);
                                            setEditVariants((prev) => {
                                              const q = Number(val);
                                              const basePrice = Number(editProductForm.price);
                                              if (!prev.length) {
                                                if (isNaN(q) || q <= 0) return prev;
                                                return [{ quantity: q, price: isNaN(basePrice) ? 0 : basePrice }];
                                              }
                                              return [{ ...prev[0], quantity: isNaN(q) ? prev[0].quantity : q }, ...prev.slice(1)];
                                            });
                                          }}
                                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                        />
                                      </Grid>
                                    </Grid>
                                    <List dense>
                                      {editVariants.map((v, idx) => (
                                        <ListItem key={`edit-var-${idx}`} sx={{ gap: 2 }}
                                          secondaryAction={
                                            <IconButton edge="end" color="error" onClick={() => removeEditVariant(idx)}>
                                              <DeleteIcon />
                                            </IconButton>
                                          }
                                        >
                                          <TextField label="Cantidad" type="number" size="small" value={v.quantity} onChange={(e) => updateEditVariantField(idx, 'quantity', e.target.value)} sx={{ mr: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                          <TextField label="Precio" type="number" size="small" value={v.price} onChange={(e) => updateEditVariantField(idx, 'price', e.target.value)} disabled={idx === 0} helperText={idx === 0 ? 'Guiado por el precio del producto' : undefined} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </ListItem>
                                      ))}
                                    </List>
                                    <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                                      <Grid item>
                                        <TextField label="Cantidad" type="number" size="small" value={editVarQty} onChange={(e) => setEditVarQty(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                      </Grid>
                                      <Grid item>
                                        <TextField label="Precio variante" type="number" size="small" value={editVarPrice} onChange={(e) => setEditVarPrice(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                      </Grid>
                                      <Grid item>
                                        <Button variant="outlined" onClick={addEditVariant} sx={{ borderRadius: '50px', textTransform: 'none' }}>Agregar variante</Button>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )}
                              </Box>

                              {/* Precios Especiales (Edición) */}
                              <Box sx={{ width: '100%', mt: 2, p: 2, bgcolor: 'white', borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Precios Especiales (Mayoreo/Temporada)</Typography>
                                
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={editDiscount.active}
                                      onChange={(e) => setEditDiscount({ ...editDiscount, active: e.target.checked })}
                                    />
                                  }
                                  label="Activar Promoción"
                                  sx={{ mb: 2 }}
                                />

                                {editDiscount.active && (
                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        select
                                        fullWidth
                                        label="Tipo de Límite"
                                        value={editDiscount.type}
                                        onChange={(e) => setEditDiscount({ ...editDiscount, type: e.target.value as any })}
                                        size="small"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                      >
                                        <MenuItem value="quantity">Límite Global (Cantidad)</MenuItem>
                                        <MenuItem value="date">Por Tiempo (Fechas)</MenuItem>
                                      </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        select
                                        fullWidth
                                        label="Tipo de Descuento"
                                        value={editDiscount.discountType}
                                        onChange={(e) => setEditDiscount({ ...editDiscount, discountType: e.target.value as any })}
                                        size="small"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                      >
                                        <MenuItem value="percentage">Porcentaje (%)</MenuItem>
                                        <MenuItem value="fixed">Monto Fijo ($)</MenuItem>
                                      </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        fullWidth
                                        label={editDiscount.discountType === 'percentage' ? "Porcentaje (%)" : "Monto a Descontar ($)"}
                                        type="number"
                                        value={editDiscount.discountValue || ''}
                                        onChange={(e) => setEditDiscount({ ...editDiscount, discountValue: Number(e.target.value) })}
                                        size="small"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                      />
                                    </Grid>

                                    {editDiscount.type === 'quantity' && (
                                      <Grid item xs={12} md={3}>
                                        <TextField
                                          fullWidth
                                          label="Límite Global de Piezas"
                                          type="number"
                                          value={editDiscount.limitCount || ''}
                                          onChange={(e) => setEditDiscount({ ...editDiscount, limitCount: Number(e.target.value) })}
                                          helperText={`Vendidos: ${editDiscount.soldCount || 0}`}
                                          size="small"
                                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                        />
                                      </Grid>
                                    )}

                                    {editDiscount.type === 'date' && (
                                      <>
                                        <Grid item xs={12} md={3}>
                                          <TextField
                                            fullWidth
                                            label="Inicio"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={editDiscount.startDate || ''}
                                            onChange={(e) => setEditDiscount({ ...editDiscount, startDate: e.target.value })}
                                            size="small"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                          <TextField
                                            fullWidth
                                            label="Fin"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={editDiscount.endDate || ''}
                                            onChange={(e) => setEditDiscount({ ...editDiscount, endDate: e.target.value })}
                                            size="small"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                          />
                                        </Grid>
                                      </>
                                    )}
                                  </Grid>
                                )}
                              </Box>

                              <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                                <Button onClick={cancelEditProduct} sx={{ borderRadius: '50px', textTransform: 'none', color: 'text.secondary' }}>Cancelar</Button>
                                <Button 
                                  variant="contained" 
                                  onClick={saveEditProduct} 
                                  sx={{ 
                                    backgroundColor: 'primary.main', 
                                    '&:hover': { backgroundColor: 'primary.dark' },
                                    borderRadius: '50px',
                                    px: 3,
                                    textTransform: 'none'
                                  }}
                                >
                                  Guardar Cambios
                                </Button>
                              </Box>
                            </Box>
                           ) : (
                             <Box sx={{ display: 'flex', alignItems: 'center', p: 2, width: '100%', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, width: '100%' }}>
                                    {p.imageUrl ? (
                                        <Box component="img" src={p.imageUrl} sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '12px', bgcolor: '#f0f0f0' }} />
                                    ) : (
                                        <Box sx={{ width: 60, height: 60, borderRadius: '12px', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <Typography variant="caption" color="text.secondary">No img</Typography>
                                        </Box>
                                    )}
                                    <Box>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {p.description}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>${p.price}</Typography>
                                    </Box>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={p.visible !== false}
                                        onChange={() => handleToggleProductVisibility(p)}
                                        size="small"
                                      />
                                    }
                                    label={<Typography variant="body2" color="text.secondary">{p.visible === false ? "Oculto" : "Visible"}</Typography>}
                                    sx={{ mr: 2 }}
                                  />
                                  <Button 
                                    onClick={() => startEditProduct(p)} 
                                    sx={{ 
                                      minWidth: 'auto', 
                                      borderRadius: '12px', 
                                      textTransform: 'none', 
                                      mr: 1 
                                    }}
                                  >
                                    Editar
                                  </Button>
                                  <IconButton 
                                    color="error" 
                                    onClick={() => handleDeleteProduct(p.id)}
                                    sx={{ bgcolor: 'rgba(211, 47, 47, 0.04)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                             </Box>
                           )}
                         </ListItem>
                       </Paper>
                     ))}
                   </List>
                </Box>
              )}

              {tab === 2 && (
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: 'text.primary', mb: 3 }}>
                    Imágenes de los Productos
                  </Typography>
                  {imgError && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{imgError}</Alert>}
                  
                  <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '24px', bgcolor: '#f8f9fa', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Subir Nueva Imagen</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={4}>
                        <TextField 
                          select 
                          fullWidth 
                          label="Producto" 
                          value={imgProductId} 
                          onChange={(e) => { setImgProductId(e.target.value); setEditImgExistingUrl(''); }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                          {products.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField 
                          fullWidth 
                          label="URL de imagen" 
                          value={imgUrl} 
                          onChange={(e) => setImgUrl(e.target.value)} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button 
                          variant="contained" 
                          onClick={handleAttachImage} 
                          sx={{ 
                            borderRadius: '50px', 
                            textTransform: 'none', 
                            backgroundColor: 'primary.main', 
                            '&:hover': { backgroundColor: 'primary.dark' },
                            width: '100%'
                          }}
                        >
                          Adjuntar
                        </Button>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Button 
                          variant="outlined"
                          onClick={() => document.getElementById('new-img-upload-input')?.click()}
                          sx={{ 
                            borderRadius: '50px', 
                            textTransform: 'none', 
                            width: '100%',
                            mt: 1
                          }}
                        >
                          {imgFile ? imgFile.name : 'Seleccionar archivo'}
                        </Button>
                        <input 
                          id="new-img-upload-input"
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => setImgFile(e.target.files?.[0] || null)} 
                          style={{ display: 'none' }} 
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button 
                          variant="contained" 
                          disabled={imgUploadLoading} 
                          onClick={handleUploadImageFile} 
                          sx={{ 
                            borderRadius: '50px', 
                            textTransform: 'none', 
                            backgroundColor: 'primary.main', 
                            '&:hover': { backgroundColor: 'primary.dark' },
                            width: '100%'
                          }}
                        >
                          {imgUploadLoading ? 'Subiendo...' : 'Subir archivo'}
                        </Button>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>Editar Imagen Existente</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label="Imagen actual"
                          value={editImgExistingUrl}
                          onChange={(e) => setEditImgExistingUrl(e.target.value)}
                          disabled={!imgProductId}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        >
                          {(products.find((p) => p.id === imgProductId)?.images || []).map((u: string, idx: number) => (
                            <MenuItem key={`${u}-${idx}`} value={u}>{u}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField 
                          fullWidth 
                          label="Nueva URL" 
                          value={editImgNewUrl} 
                          onChange={(e) => setEditImgNewUrl(e.target.value)} 
                          disabled={!editImgExistingUrl} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel control={<Checkbox checked={editImgSetAsMain} onChange={(e) => setEditImgSetAsMain(e.target.checked)} />} label="Establecer como principal" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button 
                          variant="contained" 
                          disabled={editImgLoading} 
                          onClick={handleEditImageUrl} 
                          sx={{ 
                            borderRadius: '50px', 
                            textTransform: 'none', 
                            backgroundColor: 'primary.main', 
                            '&:hover': { backgroundColor: 'primary.dark' },
                            width: '100%'
                          }}
                        >
                          {editImgLoading ? 'Guardando...' : 'Guardar cambio'}
                        </Button>
                      </Grid>
                      <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{ borderRadius: '50px', textTransform: 'none' }}
                        >
                          {editImgFile ? editImgFile.name : 'Elegir archivo'}
                          <input 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => setEditImgFile(e.target.files?.[0] || null)} 
                            style={{
                              clip: 'rect(0 0 0 0)',
                              clipPath: 'inset(50%)',
                              height: 1,
                              overflow: 'hidden',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              whiteSpace: 'nowrap',
                              width: 1,
                            }}
                          />
                        </Button>
                        <Button 
                          variant="contained" 
                          disabled={editImgLoading || !editImgFile} 
                          onClick={handleReplaceImageFile} 
                          sx={{ 
                            borderRadius: '50px', 
                            textTransform: 'none', 
                            backgroundColor: 'primary.main', 
                            '&:hover': { backgroundColor: 'primary.dark' },
                            flexGrow: 1
                          }}
                        >
                          {editImgLoading ? 'Subiendo...' : 'Subir y reemplazar'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 2 }}>Álbum del Producto</Typography>
                    {imgProductId ? (
                      (() => {
                        const current = products.find((p) => p.id === imgProductId);
                        const imgs = (current?.images || []) as string[];
                        const main = current?.imageUrl || '';
                        return imgs.length ? (
                          <Grid container spacing={2}>
                            {imgs.map((u) => (
                              <Grid item xs={6} sm={4} md={3} key={u}>
                                <Paper elevation={0} sx={{ position: 'relative', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'divider' }}>
                                  <Box component="img" src={u} alt="Imagen del producto" sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                                  {main && main === u ? (
            <Box sx={{ position: 'absolute', left: 8, top: 8, px: 1.5, py: 0.5, borderRadius: '12px', fontSize: 12, fontWeight: 600, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', backdropFilter: 'blur(4px)' }}>Principal</Box>
          ) : null}
                                  <IconButton
                                    aria-label="Eliminar imagen"
                                    size="small"
                                    onClick={() => handleDeleteProductImage(imgProductId, u)}
                                    sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(255,255,255,0.9)', color: 'error.main', '&:hover': { bgcolor: 'white' } }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                  <Box sx={{ p: 1.5 }}>
                                    <Button 
                                      size="small" 
                                      variant="outlined" 
                                      onClick={() => setProductMainImage(imgProductId, u)} 
                                      disabled={main === u}
                                      fullWidth
                                      sx={{ borderRadius: '50px', textTransform: 'none' }}
                                    >
                                      Hacer Principal
                                    </Button>
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', bgcolor: '#f8f9fa', textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Este producto no tiene imágenes adjuntas.</Typography>
                          </Paper>
                        );
                      })()
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: '12px' }}>Selecciona un producto arriba para ver su álbum.</Alert>
                    )}
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 2 }}>Imágenes en el Servidor (Storage)</Typography>
                    {storageListError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{storageListError}</Alert>}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                      <Button 
                        variant="outlined" 
                        onClick={loadStorageImagesForProduct} 
                        disabled={storageLoading || !imgProductId}
                        sx={{ borderRadius: '50px', textTransform: 'none' }}
                      > 
                        {storageLoading ? 'Cargando...' : 'Listar imágenes del servidor'} 
                      </Button>
                      {!imgProductId && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Selecciona un producto primero.</Typography>
                      )}
                    </Box>
                    {imgProductId ? (
                      storageImages.length ? (
                        <Grid container spacing={2}>
                          {storageImages.map((u) => (
                            <Grid item xs={6} sm={4} md={3} key={`storage-${u}`}>
                              <Paper elevation={0} sx={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'divider' }}>
                                <Box component="img" src={u} alt="Imagen en servidor" sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    onClick={() => attachStorageImageToProduct(u, false)}
                                    sx={{ borderRadius: '50px', textTransform: 'none' }}
                                  >
                                    Seleccionar
                                  </Button>
                                  <Button 
                                    size="small" 
                                    variant="contained" 
                                    onClick={() => attachStorageImageToProduct(u, true)} 
                                    sx={{ 
                                      borderRadius: '50px', 
                                      textTransform: 'none',
                                      backgroundColor: 'primary.main', 
                                      '&:hover': { backgroundColor: 'primary.dark' }
                                    }}
                                  >
                                    Como Principal
                                  </Button>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{storageLoading ? 'Cargando imágenes...' : 'No se encontraron imágenes en el servidor para este producto.'}</Typography>
                      )
                    ) : null}
                  </Box>
                </Box>
              )}

              {tab === 3 && (
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: 'text.primary', mb: 3 }}>
                    Zonas de Reparto
                  </Typography>
                  {zoneError && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{zoneError}</Alert>}
                  {zoneSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{zoneSuccess}</Alert>}
                  
                  <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '24px', bgcolor: '#f8f9fa', border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Configuración General</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          label="Ciudades (coma separadas)" 
                          value={zoneCities} 
                          onChange={(e) => setZoneCities(e.target.value)} 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          label="Ciudad (bloqueada)" 
                          value={zoneCityLocked} 
                          disabled 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#e0e0e0' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          label="Códigos postales (coma separadas)" 
                          value={zoneZipCodes} 
                          onChange={(e) => setZoneZipCodes(e.target.value)} 
                          helperText="Compatibilidad. Usa la tabla de abajo para configurar por CP" 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          fullWidth 
                          label="Costo de envío general (MXN)" 
                          type="number" 
                          value={zoneShippingCost} 
                          onChange={(e) => setZoneShippingCost(e.target.value)} 
                          helperText="Opcional: usado si un CP no tiene costo específico" 
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mt: 3, mb: 2 }}>Zonas por Código Postal (Atlixco)</Typography>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
                      Puedes autocompletar colonias por CP con datos de SEPOMEX. Si está activado, solo ingresa el CP y el costo.
                    </Alert>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setAutoFillColonias((v) => !v)}
                        sx={{ borderRadius: '50px', textTransform: 'none' }}
                      >
                        {autoFillColonias ? 'Autocompletar colonias: ON' : 'Autocompletar colonias: OFF'}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Fuente: SEPOMEX (NitroStudio). Si no encuentra el CP, puedes escribir las colonias manualmente.
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                      <TextField 
                        label="CP" 
                        size="small" 
                        sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
                        id="cp-input" 
                      />
                      <TextField 
                        label="Colonias (coma separadas)" 
                        size="small" 
                        sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
                        id="colonias-input" 
                        disabled={autoFillColonias} 
                        helperText={autoFillColonias ? 'Se autocompletará al agregar' : ''} 
                      />
                      <TextField 
                        label="Costo (MXN)" 
                        type="number" 
                        size="small" 
                        sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }} 
                        id="cost-input" 
                      />
                      <Button 
                        variant="contained" 
                        onClick={async () => {
                          const cpEl = document.getElementById('cp-input') as HTMLInputElement | null;
                          const colEl = document.getElementById('colonias-input') as HTMLInputElement | null;
                          const costEl = document.getElementById('cost-input') as HTMLInputElement | null;
                          const cp = (cpEl?.value || '').trim();
                          let cols = (colEl?.value || '').split(',').map((c) => c.trim()).filter(Boolean);
                          const costNum = Number(costEl?.value || '');
                          if (!cp || isNaN(costNum) || costNum < 0) {
                            setZoneError('Ingresa CP y costo válido (>= 0)');
                            return;
                          }
                          if (autoFillColonias && cols.length === 0) {
                            const fetched = await fetchColoniasByCP(cp);
                            if (fetched.length === 0) {
                              setZoneError('No se pudieron obtener colonias desde SEPOMEX para este CP. Activado modo manual para que las ingreses.');
                              setAutoFillColonias(false);
                              setTimeout(() => { (document.getElementById('colonias-input') as HTMLInputElement | null)?.focus(); }, 0);
                              return;
                            }
                            cols = fetched;
                          }
                          setZipCodeEntries((prev) => {
                            const existingIdx = prev.findIndex((e) => e.zipCode === cp);
                            const next = [...prev];
                            if (existingIdx >= 0) next[existingIdx] = { zipCode: cp, colonias: cols, cost: costNum };
                            else next.push({ zipCode: cp, colonias: cols, cost: costNum });
                            return next;
                          });
                          if (cpEl) cpEl.value = '';
                          if (colEl) colEl.value = '';
                          if (costEl) costEl.value = '';
                        }}
                        sx={{ 
                          borderRadius: '50px', 
                          textTransform: 'none', 
                          backgroundColor: 'primary.main', 
                          '&:hover': { backgroundColor: 'primary.dark' },
                          px: 3
                        }}
                      >
                        Agregar CP
                      </Button>
                    </Box>

                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '24px', overflow: 'hidden' }}>
                      <Box sx={{ display: 'flex', p: 2, bgcolor: '#f0f0f0', fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ width: 120 }}>CP</Box>
                        <Box sx={{ flex: 1 }}>Colonias</Box>
                        <Box sx={{ width: 140, textAlign: 'right' }}>Costo</Box>
                        <Box sx={{ width: 80 }}></Box>
                      </Box>
                      {zipCodeEntries.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">Sin códigos postales configurados. Agrega arriba o pega datos.</Typography>
                        </Box>
                      )}
                      {zipCodeEntries.map((e, idx) => (
                        <Box key={e.zipCode + idx} sx={{ display: 'flex', p: 2, borderTop: idx > 0 ? '1px solid #eee' : 'none', alignItems: 'center', bgcolor: 'white', '&:hover': { bgcolor: '#fafafa' } }}>
                          <Box sx={{ width: 120, fontWeight: 500 }}>{e.zipCode}</Box>
                          <Box sx={{ flex: 1, color: 'text.secondary' }}>{e.colonias.join(', ')}</Box>
                          <Box sx={{ width: 140, textAlign: 'right', fontWeight: 600 }}>{new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(e.cost)}</Box>
                          <Box sx={{ width: 80, display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton size="small" color="error" onClick={() => setZipCodeEntries((prev) => prev.filter((x) => x.zipCode !== e.zipCode))}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                    
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        variant="contained" 
                        onClick={handleSaveZones} 
                        sx={{ 
                          borderRadius: '50px', 
                          textTransform: 'none', 
                          backgroundColor: 'primary.main', 
                          '&:hover': { backgroundColor: 'primary.dark' },
                          px: 4,
                          py: 1.5
                        }}
                      >
                        Guardar Zonas
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Paper>
          )}

          {activeSection === 'bank_details' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: 'text.primary', mb: 3 }}>
                Datos Bancarios
              </Typography>
              {clabeError && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{clabeError}</Alert>}
              {clabeSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{clabeSuccess}</Alert>}
              
              <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: '24px', bgcolor: '#f8f9fa', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Configuración de Cuenta para Transferencias</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Estos datos se mostrarán en las aplicaciones móviles (iOS y Android) para pagos por transferencia.
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="Banco" 
                      value={bankName} 
                      onChange={(e) => setBankName(e.target.value)} 
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                      placeholder="Ej. BBVA Bancomer"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField 
                      fullWidth 
                      label="Beneficiario" 
                      value={beneficiary} 
                      onChange={(e) => setBeneficiary(e.target.value)} 
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                      placeholder="Ej. Blum Art S.A. de C.V."
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField 
                      fullWidth 
                      label="Número de Cuenta / CLABE" 
                      value={clabe} 
                      onChange={(e) => setClabe(e.target.value)} 
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                      placeholder="Ej. 012 345 67890123456 7"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    variant="contained" 
                    onClick={handleSaveBankDetails} 
                    disabled={clabeLoading}
                    sx={{ 
                      borderRadius: '50px', 
                      textTransform: 'none', 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      px: 4,
                      py: 1.5
                    }}
                  >
                    {clabeLoading ? 'Guardando...' : 'Guardar Datos Bancarios'}
                  </Button>
                </Box>
              </Paper>
            </Paper>
          )}

          {activeSection === 'design' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h6" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Diseño del sitio</Typography>
              {designError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{designError}</Alert>}
              {designSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>{designSuccess}</Alert>}
              <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
                Nota: Asegúrate de configurar las reglas de Firestore para permitir lectura pública en <code>settings/design</code>, de lo contrario los usuarios no verán los cambios.
              </Alert>
              {designLogoUploadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{designLogoUploadError}</Alert>}
              {designFaviconUploadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{designFaviconUploadError}</Alert>}
              <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Color primario (hex)" 
                  value={designPrimaryColor} 
                  onChange={(e) => setDesignPrimaryColor(e.target.value)} 
                  placeholder="#e91e63" 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Color acento (hex)" 
                  value={designAccentColor} 
                  onChange={(e) => setDesignAccentColor(e.target.value)} 
                  placeholder="#c2185b" 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="Color del footer" 
                  type="color" 
                  value={designFooterColor} 
                  onChange={(e) => setDesignFooterColor(e.target.value)} 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="Color de los logos" 
                  type="color" 
                  value={designLogoColor} 
                  onChange={(e) => setDesignLogoColor(e.target.value)} 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="Color de los botones" 
                  type="color" 
                  value={designButtonColor} 
                  onChange={(e) => setDesignButtonColor(e.target.value)} 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="Color del Header (Barra superior)" 
                  value={designHeaderColor} 
                  onChange={(e) => setDesignHeaderColor(e.target.value)} 
                  placeholder="#a733ff"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="Color sección servicios" 
                  value={designServicesSectionColor} 
                  onChange={(e) => setDesignServicesSectionColor(e.target.value)} 
                  placeholder="#a733ff"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Logo URL" 
                  value={designLogoUrl} 
                  onChange={(e) => setDesignLogoUrl(e.target.value)} 
                  placeholder="https://... o gs://..." 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input type="file" accept="image/*" onChange={(e) => setDesignLogoFile(e.target.files?.[0] || null)} />
                    <Button 
                      variant="contained" 
                      disabled={designLogoUploading} 
                      onClick={handleUploadLogoFile} 
                      sx={{ 
                        borderRadius: '50px', 
                        textTransform: 'none', 
                        backgroundColor: 'primary.main', 
                        '&:hover': { backgroundColor: 'primary.dark' } 
                      }}
                    >
                      {designLogoUploading ? 'Subiendo logo...' : 'Subir logo'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Medidas requeridas: Altura entre 60px y 200px. Formato: PNG (fondo transparente recomendado) o JPEG.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Favicon URL" 
                  value={designFaviconUrl} 
                  onChange={(e) => setDesignFaviconUrl(e.target.value)} 
                  placeholder="https://... o gs://..." 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="file" accept="image/*" onChange={(e) => setDesignFaviconFile(e.target.files?.[0] || null)} />
                  <Button 
                    variant="contained" 
                    disabled={designFaviconUploading} 
                    onClick={handleUploadFaviconFile} 
                    sx={{ 
                      borderRadius: '50px', 
                      textTransform: 'none', 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' } 
                    }}
                  >
                    {designFaviconUploading ? 'Subiendo favicon...' : 'Subir favicon'}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Imagen Categoría 'Todos' URL" 
                  value={designAllCategoryImageUrl} 
                  onChange={(e) => setDesignAllCategoryImageUrl(e.target.value)} 
                  placeholder="https://... o gs://..." 
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="file" accept="image/*" onChange={(e) => setDesignAllCategoryImageFile(e.target.files?.[0] || null)} />
                  <Button 
                    variant="contained" 
                    disabled={designAllCategoryImageUploading} 
                    onClick={handleUploadAllCategoryFile} 
                    sx={{ 
                      borderRadius: '50px', 
                      textTransform: 'none', 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' } 
                    }}
                  >
                    {designAllCategoryImageUploading ? 'Subiendo imagen...' : "Subir imagen 'Todos'"}
                  </Button>
                </Box>
                {designAllCategoryImageUploadError && <Alert severity="error" sx={{ mt: 1, borderRadius: '12px' }}>{designAllCategoryImageUploadError}</Alert>}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>Imágenes del Carrusel (Home)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <input type="file" accept="image/*" onChange={(e) => setDesignHomeImageFile(e.target.files?.[0] || null)} />
                  <Button 
                    variant="contained" 
                    disabled={designHomeImageUploading} 
                    onClick={handleUploadHomeImage} 
                    sx={{ 
                      borderRadius: '50px', 
                      textTransform: 'none', 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' } 
                    }}
                  >
                    {designHomeImageUploading ? 'Subiendo...' : 'Subir Imagen'}
                  </Button>
                </Box>
                {designHomeImageUploadError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{designHomeImageUploadError}</Alert>}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {designHomeImages.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 200, height: 120 }}>
                      <Box component="img" src={url} alt={`Slide ${index}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '24px' }} />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveHomeImage(index)}
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  ))}
                  {designHomeImages.length === 0 && <Typography variant="body2" color="text.secondary">No hay imágenes en el carrusel.</Typography>}
                </Box>
              </Grid>

                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Banner URL" 
                    value={designBannerUrl} 
                    onChange={(e) => setDesignBannerUrl(e.target.value)} 
                    placeholder="https://... o gs://..." 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Título de héroe" 
                    value={designHeroTitle} 
                    onChange={(e) => setDesignHeroTitle(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Subtítulo de héroe" 
                    value={designHeroSubtitle} 
                    onChange={(e) => setDesignHeroSubtitle(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                    Sección de Servicios (Textos)
                  </Typography>
                  <Grid container spacing={2}>
                    {designServicesContent.map((srv, idx) => (
                      <Grid item xs={12} md={4} key={idx}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px', bgcolor: '#f9f9f9' }}>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id={`icon-select-label-${idx}`}>Icono</InputLabel>
                            <Select
                              labelId={`icon-select-label-${idx}`}
                              value={srv.icon}
                              label="Icono"
                              onChange={(e) => {
                                const newVal = [...designServicesContent];
                                newVal[idx] = { ...newVal[idx], icon: e.target.value };
                                setDesignServicesContent(newVal);
                              }}
                              sx={{ borderRadius: '12px', bgcolor: 'white' }}
                            >
                              {ICON_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {opt.icon}
                                    <Typography>{opt.label}</Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="Título"
                            value={srv.title}
                            onChange={(e) => {
                              const newVal = [...designServicesContent];
                              newVal[idx] = { ...newVal[idx], title: e.target.value };
                              setDesignServicesContent(newVal);
                            }}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                          />
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Descripción"
                            value={srv.description}
                            onChange={(e) => {
                              const newVal = [...designServicesContent];
                              newVal[idx] = { ...newVal[idx], description: e.target.value };
                              setDesignServicesContent(newVal);
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                    Sección "Sobre Nosotros"
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Título"
                        value={designAboutTitle}
                        onChange={(e) => setDesignAboutTitle(e.target.value)}
                        placeholder="Pasión por los detalles"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="URL de la Imagen"
                          value={designAboutImageUrl}
                          onChange={(e) => setDesignAboutImageUrl(e.target.value)}
                          placeholder="https://..."
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Button
                            variant="outlined"
                            component="label"
                            sx={{ borderRadius: '50px', textTransform: 'none' }}
                          >
                            Seleccionar Imagen
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => setDesignAboutImageFile(e.target.files ? e.target.files[0] : null)}
                            />
                          </Button>
                          {designAboutImageFile && (
                            <Typography variant="caption" sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {designAboutImageFile.name}
                            </Typography>
                          )}
                          <Button
                            variant="contained"
                            disabled={!designAboutImageFile || designAboutImageUploading}
                            onClick={handleUploadAboutImageFile}
                            sx={{
                              borderRadius: '50px',
                              textTransform: 'none',
                              backgroundColor: 'primary.main',
                              '&:hover': { backgroundColor: 'primary.dark' }
                            }}
                          >
                            {designAboutImageUploading ? 'Subiendo...' : 'Subir'}
                          </Button>
                        </Box>
                        {designAboutImageUploadError && <Typography variant="caption" color="error">{designAboutImageUploadError}</Typography>}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Descripción"
                        value={designAboutDescription}
                        onChange={(e) => setDesignAboutDescription(e.target.value)}
                        placeholder="En Blüm, entendemos que..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                    Configuración de Soporte
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="WhatsApp de Soporte (Botón de Ayuda)"
                        value={designSupportPhone}
                        onChange={(e) => setDesignSupportPhone(e.target.value)}
                        placeholder="522222222222"
                        helperText="Número con código de país (ej. 521234567890)"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                    Pie de Página (Footer)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Descripción de la empresa"
                        value={designFooterDescription}
                        onChange={(e) => setDesignFooterDescription(e.target.value)}
                        placeholder="Los mejores arreglos florales..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Teléfono"
                        value={designFooterPhone}
                        onChange={(e) => setDesignFooterPhone(e.target.value)}
                        placeholder="+52 222..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={designFooterEmail}
                        onChange={(e) => setDesignFooterEmail(e.target.value)}
                        placeholder="contacto@..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Horario"
                        value={designFooterHours}
                        onChange={(e) => setDesignFooterHours(e.target.value)}
                        placeholder="Lunes a Domingo..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Facebook URL"
                        value={designFooterFacebook}
                        onChange={(e) => setDesignFooterFacebook(e.target.value)}
                        placeholder="https://facebook.com/..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Instagram URL"
                        value={designFooterInstagram}
                        onChange={(e) => setDesignFooterInstagram(e.target.value)}
                        placeholder="https://instagram.com/..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="WhatsApp URL"
                        value={designFooterWhatsapp}
                        onChange={(e) => setDesignFooterWhatsapp(e.target.value)}
                        placeholder="https://wa.me/..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 3, borderRadius: '24px', border: '1px solid', borderColor: 'divider', background: '#fafafa' }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Vista previa rápida</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: designPrimaryColor }} />
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: designAccentColor }} />
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', backgroundColor: designFooterColor }} />
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', backgroundColor: designLogoColor }} />
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', backgroundColor: designButtonColor || designPrimaryColor }} />
                      {designLogoUrl && (
                        <Box component="img" src={designLogoUrl} alt="Logo" sx={{ height: 40 }} />
                      )}
                      {designFaviconUrl && (
                        <Box component="img" src={designFaviconUrl} alt="Favicon" sx={{ height: 32, width: 32 }} />
                      )}
                      <Typography sx={{ color: designPrimaryColor, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>{designHeroTitle || 'Título ejemplo'}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    onClick={handleSaveDesign} 
                    sx={{ 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: '50px',
                      textTransform: 'none',
                      px: 4,
                      py: 1.5,
                      fontWeight: 600
                    }}
                  >
                    Guardar Diseño
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {activeSection === 'realtime' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Pedidos en tiempo real</Typography>
              {ordersError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{ordersError}</Alert>}
              {ordersNotice && <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>{ordersNotice}</Alert>}
              {(() => {
                const activeOrders = orders.filter((o) => {
                  const s = (o.status || '').toLowerCase();
                  return s !== 'entregado' && s !== 'delivered';
                });

                if (activeOrders.length === 0) {
                  return <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 2 }}>No hay pedidos pendientes.</Typography>;
                }

                // Helpers
                const getDate = (o: any) => o.shipping?.deliveryDate || o.deliveryDate || '9999-12-31';
                const getTimeOrder = (t: string) => {
                  if (t.startsWith('9') || t.startsWith('09')) return 0;
                  if (t.startsWith('14')) return 1;
                  if (t.startsWith('18')) return 2;
                  return 3;
                };
                const getTimeLabel = (t: string) => {
                  if (t.startsWith('9') || t.startsWith('09')) return 'Mañana (9:00 - 12:00)';
                  if (t.startsWith('14')) return 'Tarde (14:00 - 18:00)';
                  if (t.startsWith('18')) return 'Noche (18:00 - 21:00)';
                  return t;
                };

                // Group by Date
                const groupedByDate: { [date: string]: typeof activeOrders } = {};
                activeOrders.forEach(o => {
                  const date = getDate(o);
                  if (!groupedByDate[date]) groupedByDate[date] = [];
                  groupedByDate[date].push(o);
                });

                // Sort dates (ASC)
                const sortedDates = Object.keys(groupedByDate).sort();

                // Calculate today YYYY-MM-DD
                const now = new Date();
                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

                return (
                  <List>
                    {sortedDates.map((date) => {
                      const groupOrders = groupedByDate[date];
                      
                      // Format Header
                      let headerLabel = date;
                      let isToday = date === todayStr;
                      if (isToday) headerLabel = 'Entrega para hoy';
                      else if (date === '9999-12-31') headerLabel = 'Sin fecha programada';
                      else {
                         const [y, m, d] = date.split('-');
                         if (y && m && d) headerLabel = `Entrega: ${d}/${m}/${y}`;
                      }

                      // Group by Time within Date
                      const groupedByTime: { [time: string]: typeof groupOrders } = {};
                      groupOrders.forEach(o => {
                        const t = (o.shipping?.deliveryTime || o.deliveryTime || 'Sin horario').toString();
                        if (!groupedByTime[t]) groupedByTime[t] = [];
                        groupedByTime[t].push(o);
                      });

                      // Sort Time Keys
                      const timeKeys = Object.keys(groupedByTime).sort((a, b) => getTimeOrder(a) - getTimeOrder(b));

                      return (
                        <Box key={date} sx={{ mb: 4 }}>
                          <Box sx={{ 
                            bgcolor: isToday ? '#e3f2fd' : '#f5f5f5', 
                            px: 2, py: 1.5, 
                            borderRadius: '12px',
                            mb: 2,
                            borderLeft: isToday ? '4px solid #1976d2' : '4px solid #bdbdbd',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            {isToday && <Typography variant="h6">🚀</Typography>}
                            <Typography variant="h6" sx={{ fontWeight: 700, color: isToday ? '#1565c0' : 'text.primary', fontSize: '1.1rem' }}>
                              {headerLabel}
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600, color: 'text.secondary' }}>
                              {groupOrders.length} pedido(s)
                            </Typography>
                          </Box>

                          {timeKeys.map(timeSlot => (
                            <Box key={timeSlot} sx={{ mb: 2, ml: { xs: 0, md: 2 } }}>
                              <Typography variant="subtitle2" sx={{ 
                                color: 'primary.main', 
                                mb: 1, 
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                letterSpacing: '0.05em',
                                borderBottom: '1px dashed #eee',
                                pb: 0.5,
                                display: 'inline-block'
                              }}>
                                ⏰ {getTimeLabel(timeSlot)}
                              </Typography>
                              
                              {groupedByTime[timeSlot].map((o) => {
                                const statusInfo = getStatusColor(o.status);
                                const isAnon = Boolean(o.anonymous || (o.shippingAddress as any)?.anonymous || (o.address as any)?.anonymous || (o.shipping as any)?.anonymous);
                                
                                return (
                                  <ListItem key={o.id} divider sx={{ px: 0, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'flex-start', lg: 'center' }, gap: 2, py: 2 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                         <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Playfair Display", serif' }}>
                                           {`Pedido #${o.id}`}
                                         </Typography>
                                         <Box sx={{ px: 1, py: 0.2, borderRadius: '6px', bgcolor: statusInfo.bg, color: statusInfo.color, fontSize: '0.75rem', fontWeight: 600 }}>
                                            {statusInfo.label}
                                         </Box>
                                      </Box>
                                      <Typography variant="body2" color="text.secondary">
                                        {`Total: $${typeof o.total === 'number' ? o.total.toFixed(2) : '—'}`}
                                      </Typography>
                                      
                                      {(() => {
                                        const msg: string = (o?.shipping?.message || (o as any)?.message || '').toString();
                                        return msg ? (
                                          <Box sx={{ mt: 1.5, borderLeft: '3px solid #C084FC', pl: 2, py: 0.5, bgcolor: 'rgba(192, 132, 252, 0.05)', borderRadius: '0 8px 8px 0' }}>
                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>"{msg}"</Typography>
                                          </Box>
                                        ) : null;
                                      })()}
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', lg: 'auto' }, flexWrap: 'wrap', mt: { xs: 2, lg: 0 } }}>
                                      <TextField 
                                        select 
                                        label="Actualizar Estado" 
                                        size="small" 
                                        value={o.status || 'Paid'} 
                                        onChange={(e) => updateOrderStatus(o, e.target.value)} 
                                        sx={{ 
                                          minWidth: 180,
                                          '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                                        }}
                                      >
                                        <MenuItem value="Paid" sx={{ color: '#1976d2' }}>Pagado</MenuItem>
                                        <MenuItem value="En preparación" sx={{ color: '#f57c00' }}>En preparación</MenuItem>
                                        <MenuItem value="Enviado" sx={{ color: '#7b1fa2' }}>Enviado</MenuItem>
                                        <MenuItem value="Entregado" sx={{ color: '#388e3c' }}>Entregado</MenuItem>
                                      </TextField>

                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: isAnon ? '#4caf50' : '#e0e0e0', borderRadius: '50px', px: 1.5, py: 0.5, bgcolor: isAnon ? 'rgba(76, 175, 80, 0.08)' : 'transparent' }}>
                                        {isAnon ? (
                                          <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />
                                        ) : (
                                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>
                                        )}
                                        <Typography variant="body2" fontWeight={500} sx={{ color: isAnon ? '#2e7d32' : 'text.secondary' }}>
                                          {isAnon ? 'Anónimo' : 'No anónimo'}
                                        </Typography>
                                      </Box>

                                      <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={() => setOpenOrderDetail(o)}
                                        sx={{ 
                                          borderRadius: '50px',
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          minWidth: '100px'
                                        }}
                                      >
                                        Detalles
                                      </Button>
                                    </Box>
                                  </ListItem>
                                );
                              })}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </List>
                );
              })()}
            </Paper>
          )}

          {activeSection === 'previous' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Pedidos anteriores</Typography>
              {ordersError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{ordersError}</Alert>}
              {ordersNotice && <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>{ordersNotice}</Alert>}
              <List>
                {orders
                  .filter((o) => {
                    const s = (o.status || '').toLowerCase();
                    return s === 'entregado' || s === 'delivered';
                  })
                  .map((o) => (
                    <ListItem key={o.id} divider sx={{ px: 0 }}>
                      <ListItemText 
                        primary={<Typography fontWeight={600}>{`Pedido ${o.id} • ${o.status || '—'}`}</Typography>} 
                        secondary={`Total: ${typeof o.total === 'number' ? o.total : '—'}`} 
                      />
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => updateOrderStatus(o, 'En preparación')} 
                        sx={{ 
                          ml: 2, 
                          backgroundColor: 'primary.main', 
                          '&:hover': { backgroundColor: 'primary.dark' },
                          borderRadius: '50px',
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 2
                        }}
                      >
                        Regresar a prod
                      </Button>
                    </ListItem>
                  ))}
              </List>
          </Paper>
        )}

        {/* Dialog: Detalles completos del pedido (tiempo real) */}
        <Dialog 
          open={Boolean(openOrderDetail)} 
          onClose={() => setOpenOrderDetail(null)} 
          fullWidth 
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
        >
          <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Detalles del pedido</DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.05)' }}>
            {openOrderDetail ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(() => {
                  const ts = openOrderDetail?.createdAt?.toDate?.() || null;
                  const fecha = ts ? ts.toLocaleString() : '—';
                  const total = typeof openOrderDetail?.total === 'number' ? openOrderDetail.total : '—';
                  const status = (openOrderDetail?.status || '').toString();
                  const shipping = (openOrderDetail?.shippingAddress || openOrderDetail?.address || openOrderDetail?.shipping || {}) as any;
                  const isAnon = Boolean(openOrderDetail?.anonymous || shipping?.anonymous);
                  const recipientName = (openOrderDetail?.recipient?.name || openOrderDetail?.recipientName || shipping?.recipientName || '') as string;
                  const recipientPhone = (openOrderDetail?.recipient?.phone || openOrderDetail?.recipientPhone || shipping?.recipientPhone || '') as string;
                  const items = Array.isArray(openOrderDetail?.items) ? openOrderDetail.items : [];
                  
                  const sender = openOrderDetail?.sender || {};
                  const senderName = sender.name || openOrderDetail?.userName || '—';
                  const senderEmail = sender.email || openOrderDetail?.userEmail || '—';
                  const senderPhone = sender.phone || '—';

                  return (
                    <>
                      <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Pedido {openOrderDetail.id}</Typography>
                      <Typography variant="body2">Fecha: {fecha}</Typography>
                      <Typography variant="body2">Estado: {status || '—'}</Typography>
                      <Typography variant="body2">Envío: {typeof openOrderDetail.shippingCost === 'number' ? `$${openOrderDetail.shippingCost.toFixed(2)}` : '—'}</Typography>
                      <Typography variant="body2">Total: {total}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #C084FC', borderRadius: '12px', px: 1.5, py: 0.5 }}>
                          {isAnon ? (
                            <CheckCircle sx={{ color: 'green', fontSize: 20 }} />
                          ) : (
                            <Cancel sx={{ color: 'red', fontSize: 20 }} />
                          )}
                          <Typography variant="body2" fontWeight={500}>{isAnon ? 'Envío anónimo' : 'No anónimo'}</Typography>
                        </Box>
                      </Box>

                      {!isAnon && (
                        <>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Datos del Remitente</Typography>
                          <Typography variant="body2">Nombre: {senderName}</Typography>
                          <Typography variant="body2">Email: {senderEmail}</Typography>
                          <Typography variant="body2">Teléfono: {senderPhone}</Typography>
                        </>
                      )}

                      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Destinatario</Typography>
                      <Typography variant="body2">Nombre: {recipientName || '—'}</Typography>
                      <Typography variant="body2">Teléfono: {recipientPhone || '—'}</Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Dirección de entrega</Typography>
                      <Typography variant="body2">{`${shipping?.street || ''}${shipping?.colonia ? `, Col. ${shipping.colonia}` : ''}` || '—'}</Typography>
                      <Typography variant="body2">{`${shipping?.city || ''}${shipping?.state ? `, ${shipping.state}` : ''}${shipping?.zipCode ? ` ${shipping.zipCode}` : ''}` || ''}</Typography>
                      {(shipping?.deliveryDate || shipping?.deliveryTime) && (
                        <Typography variant="body2">Entrega: {`${shipping?.deliveryDate || ''}${shipping?.deliveryTime ? ` — ${shipping.deliveryTime}` : ''}`}</Typography>
                      )}
                      {(() => {
                        const msg: string = (shipping?.message || (openOrderDetail as any)?.message || '').toString();
                        return msg ? (
                          <>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Mensaje del cliente</Typography>
                            <Box sx={{ border: '1px solid #C084FC', borderRadius: '12px', px: 2, py: 1, bgcolor: 'rgba(192, 132, 252, 0.05)' }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontStyle: 'italic' }}>"{msg}"</Typography>
                            </Box>
                          </>
                        ) : null;
                      })()}
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Artículos</Typography>
                      {items.length ? (
                        <List>
                          {items.map((it: any, idx: number) => (
                            <ListItem key={idx} divider sx={{ px: 0 }}>
                              <ListItemText
                                primary={<Typography fontWeight={600}>{(it?.title || it?.name || it?.productName || 'Artículo').toString()}</Typography>}
                                secondary={`Cantidad: ${typeof it?.quantity === 'number' ? it.quantity : '—'} • Precio: ${typeof it?.price === 'number' ? `$${it.price.toFixed(2)}` : '—'}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin artículos.</Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenOrderDetail(null)}
              sx={{ 
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

          {activeSection === 'coupons' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Cupones</Typography>
              {couponError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{couponError}</Alert>}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    fullWidth 
                    label="Código" 
                    value={couponCode} 
                    onChange={(e) => setCouponCode(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    select 
                    fullWidth 
                    label="Tipo" 
                    value={couponType} 
                    onChange={(e) => setCouponType(e.target.value as any)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  >
                    <MenuItem value="value">Por valor</MenuItem>
                    <MenuItem value="percentage">Por porcentaje</MenuItem>
                    <MenuItem value="free_shipping">Envío Gratis</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    fullWidth 
                    label={couponType === 'percentage' ? 'Porcentaje (%)' : 'Valor'} 
                    value={couponAmount} 
                    onChange={(e) => setCouponAmount(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel control={<Checkbox checked={couponActive} onChange={(e) => setCouponActive(e.target.checked)} />} label="Activo" />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Descripción (opcional)" 
                    value={couponDesc} 
                    onChange={(e) => setCouponDesc(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    onClick={handleAddCoupon} 
                    sx={{ 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5
                    }}
                  >
                    Crear cupón
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Lista de cupones</Typography>
              <List>
                {coupons.map((c) => (
                  <ListItem key={c.id} divider sx={{ px: 0 }}>
                    <ListItemText 
                      primary={
                        <Typography fontWeight={600}>
                          {`${c.code} • ${
                            c.type === 'free_shipping' 
                              ? 'Envío Gratis' 
                              : c.type === 'percentage' 
                                ? c.amount + '%' 
                                : '$' + c.amount
                          }`}
                        </Typography>
                      } 
                      secondary={`Activo: ${c.active ? 'Sí' : 'No'}${c.description ? ' • ' + c.description : ''}`} 
                    />
                    <Button 
                      variant="contained" 
                      size="small" 
                      onClick={() => toggleCouponActive(c)} 
                      sx={{ 
                        mr: 1, 
                        backgroundColor: 'primary.main', 
                        '&:hover': { backgroundColor: 'primary.dark' },
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      {c.active ? 'Desactivar' : 'Activar'}
                    </Button>
                    <IconButton onClick={() => deleteCoupon(c)} aria-label="delete" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {activeSection === 'push' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Enviar Notificaciones Push</Typography>
              {pushError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{pushError}</Alert>}
              {pushSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>{pushSuccess}</Alert>}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Título" 
                    value={pushTitle} 
                    onChange={(e) => setPushTitle(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Cuerpo" 
                    value={pushBody} 
                    onChange={(e) => setPushBody(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Imagen (URL opcional)" 
                    value={pushImageUrl} 
                    onChange={(e) => setPushImageUrl(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Deep Link (opcional)" 
                    placeholder="Ej: product/ID_PRODUCTO o orders" 
                    helperText="Rutas soportadas: product/{id}, orders (Mis Pedidos)"
                    value={pushDeepLink} 
                    onChange={(e) => setPushDeepLink(e.target.value)} 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    select 
                    fullWidth 
                    label="Destino" 
                    value={pushTarget} 
                    onChange={(e) => setPushTarget(e.target.value as any)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="userId">Por UID de usuario</MenuItem>
                  </TextField>
                </Grid>
                {pushTarget === 'userId' && (
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      options={clients}
                      getOptionLabel={(option) => `${option.name || 'Sin nombre'} (${option.email || 'Sin email'})`}
                      value={clients.find((c) => c.id === pushUserId) || null}
                      onChange={(_, newValue) => setPushUserId(newValue ? newValue.id : '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar Usuario"
                          placeholder="Nombre, email o ID"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="subtitle2">{option.name || 'Sin nombre'}</Typography>
                            <Typography variant="body2" color="text.secondary">{option.email || 'Sin email'}</Typography>
                            <Typography variant="caption" color="text.disabled">{option.id}</Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    disabled={pushSending} 
                    onClick={handleSendPush} 
                    sx={{ 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5
                    }}
                  >
                    {pushSending ? 'Enviando…' : 'Enviar Notificación'}
                  </Button>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ borderRadius: '12px' }}>
                Las notificaciones se encolan en Firestore (colección <strong>pushQueue</strong>). Un backend o Cloud Function debe procesarlas y enviarlas vía FCM.
              </Alert>
            </Paper>
          )}

          {activeSection === 'stripe' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Configuración de Stripe</Typography>
              {stripeError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{stripeError}</Alert>}
              {stripeSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>{stripeSuccess}</Alert>}
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
                    Advertencia: Estas llaves son críticas para el procesamiento de pagos. Asegúrate de usar las llaves correctas (Test o Live) según el entorno.
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Llave Pública (Publishable Key)" 
                    value={stripePublicKey} 
                    onChange={(e) => setStripePublicKey(e.target.value)} 
                    placeholder="pk_..."
                    helperText="Comienza con pk_"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Llave Secreta (Secret Key)" 
                    value={stripeSecretKey} 
                    onChange={(e) => setStripeSecretKey(e.target.value)} 
                    placeholder="sk_..."
                    helperText="Comienza con sk_"
                    type="password"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    disabled={stripeLoading} 
                    onClick={handleSaveStripeKeys} 
                    sx={{ 
                      backgroundColor: 'primary.main', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5
                    }}
                  >
                    {stripeLoading ? 'Guardando...' : 'Guardar Llaves'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {activeSection === 'telegram' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Integración con Telegram</Typography>
              {telegramError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{telegramError}</Alert>}
              {telegramSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>{telegramSuccess}</Alert>}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Bot Token" 
                    value={telegramToken} 
                    onChange={(e) => setTelegramToken(e.target.value)} 
                    placeholder="12345678:AA..." 
                    type="password"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth 
                    label="Chat ID" 
                    value={telegramChatId} 
                    onChange={(e) => setTelegramChatId(e.target.value)} 
                    placeholder="-1001234567890"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button 
                      variant="contained" 
                      disabled={telegramLoading} 
                      onClick={handleSaveTelegram} 
                      sx={{ 
                        backgroundColor: 'primary.main', 
                        '&:hover': { backgroundColor: 'primary.dark' },
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5
                      }}
                    >
                      {telegramLoading ? 'Guardando...' : 'Guardar Configuración'}
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={handleTestTelegram}
                      sx={{
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5
                      }}
                    >
                      Enviar Prueba
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {activeSection === 'clients' && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Clientes</Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                   <Box sx={{ bgcolor: 'primary.main', color: 'white', px: 2, py: 0.5, borderRadius: '50px', fontSize: '0.875rem', fontWeight: 600 }}>
                     {clients.length} Total
                   </Box>
                   <TextField
                    select
                    size="small"
                    value={clientsSort}
                    onChange={(e) => setClientsSort(e.target.value as any)}
                    sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  >
                    <MenuItem value="date">Fecha de registro</MenuItem>
                    <MenuItem value="alpha">Alfabético (A-Z)</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                  </TextField>
                </Box>
              </Box>

              <List>
                {clients
                  .sort((a, b) => {
                    if (clientsSort === 'alpha') return (a.name || '').localeCompare(b.name || '');
                    if (clientsSort === 'email') return (a.email || '').localeCompare(b.email || '');
                    // Date sort (newest first)
                    const getMs = (val: any) => {
                      if (!val) return 0;
                      if (typeof val.toMillis === 'function') return val.toMillis();
                      if (typeof val === 'number') return val; // timestamp
                      if (typeof val === 'string') return new Date(val).getTime();
                      if (val.seconds) return val.seconds * 1000;
                      return 0;
                    };
                    return getMs(b.createdAt) - getMs(a.createdAt);
                  })
                  .map((c) => {
                  const name = (c.name || '').toString();
                  const email = (c.email || '').toString();
                  const phone = (c.phone || '').toString();
                  const recipients = Array.isArray(recipientsByUser[c.id]) ? recipientsByUser[c.id] : [];
                  const ordersCount = openOrdersClientId === c.id && Array.isArray(ordersModalList)
                    ? ordersModalList.length
                    : (typeof orderCountsByUser[c.id] === 'number' ? orderCountsByUser[c.id] : 0);
                  return (
                    <ListItem key={c.id} alignItems="flex-start" divider sx={{ px: 0 }}>
                      <ListItemText
                        primary={<Typography fontWeight={600}>{name || email || `Cliente ${c.id}`}</Typography>}
                        secondary={`Email: ${email || '—'} • Tel: ${phone || '—'}`}
                      />
                      <Box sx={{ ml: 2, minWidth: 300, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>Destinatarios: {recipients.length} • Pedidos: {ordersCount}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="contained" 
                            size="small" 
                            onClick={() => setOpenAddressesClientId(c.id)} 
                            sx={{ 
                              backgroundColor: 'primary.main', 
                              '&:hover': { backgroundColor: 'primary.dark' },
                              borderRadius: '50px',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 2
                            }}
                          >
                            Ver destinatarios
                          </Button>
                          <Button 
                            variant="contained" 
                            size="small" 
                            onClick={() => setOpenOrdersClientId(c.id)} 
                            sx={{ 
                              backgroundColor: 'primary.main', 
                              '&:hover': { backgroundColor: 'primary.dark' },
                              borderRadius: '50px',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 2
                            }}
                          >
                            Ver pedidos
                          </Button>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>

              {/* Dialog: Destinatarios del cliente */}
              <Dialog 
                open={Boolean(openAddressesClientId)} 
                onClose={() => setOpenAddressesClientId(null)} 
                fullWidth 
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
              >
                <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Destinatarios del cliente</DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                  {openAddressesClientId ? (
                    (() => {
                      const list = Array.isArray(recipientsByUser[openAddressesClientId]) ? recipientsByUser[openAddressesClientId] : [];
                      return list.length ? (
                        <List>
                          {list.map((rec: any) => (
                            <ListItem key={rec.id} divider>
                              <ListItemText
                                primary={<Typography fontWeight={600}>{`${rec.name ? rec.name + ' — ' : ''}${rec.street || ''}${rec.colonia ? `, Col. ${rec.colonia}` : ''}`}</Typography>}
                                secondary={`${rec.city || ''}${rec.state ? `, ${rec.state}` : ''}${rec.zipCode ? ` ${rec.zipCode}` : ''}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin destinatarios registrados.</Typography>
                      );
                    })()
                  ) : null}
                </DialogContent>
                <DialogActions>
                  <Button 
                    onClick={() => setOpenAddressesClientId(null)}
                    sx={{ 
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Cerrar
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Dialog: Pedidos del cliente */}
              <Dialog 
                open={Boolean(openOrdersClientId)} 
                onClose={() => setOpenOrdersClientId(null)} 
                fullWidth 
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
              >
                <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>Pedidos del cliente</DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                  {openOrdersClientId ? (
                    (() => {
                      const list = Array.isArray(ordersModalList) ? ordersModalList : [];
                      return list.length ? (
                        <List>
                          {list.map((o: any) => {
                            const ts = o.createdAt?.toDate?.() || null;
                            const fecha = ts ? ts.toLocaleString() : '—';
                            const total = typeof o.total === 'number' ? o.total : 0;
                            const status = (o.status || '').toString();
                            return (
                              <ListItem key={o.id} divider>
                                <ListItemText primary={<Typography fontWeight={600}>{`Pedido ${o.id} • ${status || 'pendiente'}`}</Typography>} secondary={`Fecha: ${fecha} • Total: ${total}`} />
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  onClick={() => setOpenOrderDetail(o)}
                                  sx={{ 
                                    borderRadius: '50px',
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  Mostrar detalles del pedido
                                </Button>
                              </ListItem>
                            );
                          })}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin pedidos registrados.</Typography>
                      );
                    })()
                  ) : null}
                </DialogContent>
                <DialogActions>
                  <Button 
                    onClick={() => setOpenOrdersClientId(null)}
                    sx={{ 
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Cerrar
                  </Button>
                </DialogActions>
              </Dialog>

            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminPage;
